// admin-dashboard/src/App.js — Mon Dépanneur CI V3 Admin
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  LayoutDashboard, BadgeCheck, CreditCard, AlertTriangle, Scale, MessageCircle,
  Wrench, Hammer, Wallet, Users, Megaphone, Bell, ShieldCheck, FileText, Settings,
  User, Star, Sparkles, MessageSquare, Handshake, Car, MapPin, Flag, Ban, XCircle,
  Crown, Calculator, LifeBuoy, Search, BarChart3, Banknote, Trophy, Zap, Bot, Plus,
  TrendingUp, Clock, Paperclip, Key, Lock, Smartphone, AlertCircle, Shuffle, Mail,
  Shield, Target, Rocket, Loader2, DollarSign,
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const api = axios.create({ baseURL: API });

api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('admin_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});
api.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      window.location.reload();
    }
    return Promise.reject(err);
  }
);

// ─── THEME — tokens MD-UI v4 (alignés sur l'app mobile) ─────────
const C = {
  // Indigo électrique — action primaire  [accent]  (charte Indigo & Cyan, 2026-08-11)
  green: '#2F5BFF', greenD: '#1E3FCC', greenL: '#DDE5FF',
  // Cyan — accent secondaire / mise en avant  [success]
  gold: '#0E8AA8', goldD: '#0B6E85', goldL: '#D7EEF5', goldTint: '#F0F4FF',
  // Cyan vif — succès / KYC vérifié  [success]
  success: '#0E8AA8', successL: '#D7EEF5',
  // Indigo profond — premium / confiance, badge PRO
  premium: '#0B1F5C', premiumL: '#D8DFF7',
  // Fond / panels — indigo profond pour la lisibilité technique
  dark: '#101A3D', panel: '#16224D', border: '#26305F',
  // Texte  [ink]
  ink: '#101426', inkSoft: '#4A5470', gray: '#8A93AD',
  line: '#E1E5EF', canvas: '#F5F7FC', white: '#FFFFFF',
  red: '#DC2626', redL: '#FEF2F2',
  blue: '#2F5BFF', blueL: '#DDE5FF',
};

/** Icône lucide-react colorée, à la place des anciens emojis. */
function Ic({ icon: IconCmp, color = C.ink, size = 16, style }) {
  if (!IconCmp) return null;
  return <IconCmp size={size} color={color} strokeWidth={2.25} style={{ flexShrink: 0, ...style }} />;
}

/**
 * Filet de sécurité : si une page plante au rendu, affiche un message
 * à la place de cette page au lieu de faire disparaître tout le dashboard
 * (le reste — menu, barre du haut — reste utilisable). Rappel : une erreur
 * survenue le 2026-08-16 dans le menu latéral avait fait planter l'app
 * entière, sans filet, en l'absence d'une error boundary comme celle-ci.
 */
class PageErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('PageErrorBoundary', error, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, textAlign: 'center', color: '#4A5470' }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
            Cette page a rencontré une erreur.
          </div>
          <div style={{ fontSize: 12.5, marginBottom: 16 }}>
            Essaie une autre page dans le menu, ou recharge le dashboard.
          </div>
          <button style={s.btn()} onClick={() => window.location.reload()}>Recharger</button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Thème clair / sombre.
 *
 * Seule la coque (barre, menu latéral) change : la zone de contenu est
 * déjà claire dans les deux modes. Les couleurs passent par des variables
 * CSS pour qu'un seul basculement suffise, sans reconstruire les styles.
 */
const THEMES = {
  dark: {
    '--shell-bg':      '#0A1230',
    '--shell-panel':   'rgba(19,28,64,.82)',
    '--shell-border':  'rgba(255,255,255,.09)',
    '--shell-text':    '#E7EBFA',
    '--sidebar-bg':    'rgba(13,20,50,.72)',
    '--sidebar-label': '#5C6A9A',
    '--sidebar-item':  '#9FA8D9',
    '--sidebar-active-bg':   'rgba(47,91,255,.18)',
    '--sidebar-active-text': '#93A9FF',
    '--brand-text':    '#FFFFFF',
    // Contenu : indigo anthracite plutôt que blanc — moins agressif de nuit
    '--main-bg':       '#0D1B3D',
    '--surface':       'rgba(255,255,255,.045)',
    '--surface-solid': '#142452',
    '--card-border':   'rgba(255,255,255,.10)',
    '--card-border-hover': 'rgba(47,91,255,.42)',
    '--card-shadow':       '0 1px 2px rgba(0,0,0,.4), 0 12px 32px -12px rgba(0,0,0,.6)',
    '--card-shadow-hover': '0 1px 2px rgba(0,0,0,.4), 0 18px 44px -14px rgba(0,0,0,.7)',
    '--text-strong':   '#EEF1FA',
    '--text-soft':     '#A9B4D9',
    '--text-mute':     '#6F7CA3',
    '--hairline':      'rgba(255,255,255,.08)',
    '--input-bg':      'rgba(255,255,255,.05)',
    '--th-bg':         'rgba(255,255,255,.035)',
    '--row-hover':     'rgba(47,91,255,.08)',
    '--glow-a':        'rgba(47,91,255,.16)',
    '--glow-b':        'rgba(14,138,168,.14)',
    '--scroll-thumb':  'rgba(159,168,217,.28)',
    '--scroll-thumb-hover': 'rgba(159,168,217,.5)',
  },
  light: {
    '--shell-bg':      '#EEF0FA',
    '--shell-panel':   'rgba(255,255,255,.88)',
    '--shell-border':  'rgba(16,20,66,.10)',
    '--shell-text':    '#101426',
    '--sidebar-bg':    'rgba(255,255,255,.72)',
    '--sidebar-label': '#93A0C7',
    '--sidebar-item':  '#4A5C8A',
    '--sidebar-active-bg':   'rgba(47,91,255,.14)',
    '--sidebar-active-text': '#2F5BFF',
    '--brand-text':    '#101A3D',
    '--main-bg':       'transparent',
    '--surface':       'rgba(255,255,255,.86)',
    '--surface-solid': '#FFFFFF',
    '--card-border':   'rgba(16,20,66,.08)',
    '--card-border-hover': 'rgba(47,91,255,.34)',
    '--card-shadow':       '0 1px 2px rgba(16,20,66,.05), 0 10px 28px -14px rgba(16,20,66,.22)',
    '--card-shadow-hover': '0 1px 2px rgba(16,20,66,.06), 0 18px 40px -16px rgba(16,20,66,.28)',
    '--text-strong':   '#101426',
    '--text-soft':     '#4A5470',
    '--text-mute':     '#828FB8',
    '--hairline':      'rgba(16,20,66,.08)',
    '--input-bg':      '#FFFFFF',
    '--th-bg':         'rgba(16,20,66,.035)',
    '--row-hover':     'rgba(47,91,255,.06)',
    '--glow-a':        'rgba(47,91,255,.13)',
    '--glow-b':        'rgba(14,138,168,.12)',
    '--scroll-thumb':  'rgba(74,84,138,.26)',
    '--scroll-thumb-hover': 'rgba(74,84,138,.45)',
  },
};

function applyTheme(mode) {
  const vars = THEMES[mode] || THEMES.dark;
  Object.entries(vars).forEach(([k, v]) =>
    document.documentElement.style.setProperty(k, v)
  );
  document.body.style.background = vars['--shell-bg'];
}

// Appliqué dès le chargement du module : sans ça, la page clignote une
// fraction de seconde dans le thème par défaut avant le premier rendu.
applyTheme(localStorage.getItem('admin_theme') || 'dark');

const s = {
  app: {
    height: '100vh', overflow: 'hidden', background: 'transparent',
    color: 'var(--shell-text)', display: 'flex', flexDirection: 'column',
  },
  topbar: {
    background: 'var(--shell-panel)',
    backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
    borderBottom: '1px solid var(--shell-border)',
    padding: '13px 26px', display: 'flex', alignItems: 'center', gap: 16,
    position: 'sticky', top: 0, zIndex: 20,
  },
  brand: {
    display: 'flex', alignItems: 'center', gap: 10,
    color: 'var(--brand-text)', fontWeight: 800, fontSize: 16,
    letterSpacing: '-0.3px',
  },
  brandIco: {
    width: 32, height: 32,
    // Dégradé indigo→cyan : les deux couleurs de la marque, pas un aplat
    background: `linear-gradient(135deg, ${C.green} 0%, ${C.success} 55%, ${C.premium} 140%)`,
    borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 15, boxShadow: '0 4px 14px -4px rgba(47,91,255,.55)',
  },
  layout: { display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' },
  sidebar: {
    width: 232, background: 'var(--sidebar-bg)',
    backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
    borderRight: '1px solid var(--shell-border)',
    overflowY: 'auto', flexShrink: 0, padding: '14px 10px',
  },
  sbSection: {
    padding: '14px 12px 6px', fontSize: 9.5, fontWeight: 700,
    color: 'var(--sidebar-label)', textTransform: 'uppercase', letterSpacing: '1px',
  },
  sbItem: (active) => ({
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 12px', cursor: 'pointer', fontSize: 12.5,
    fontWeight: active ? 700 : 500,
    color: active ? 'var(--sidebar-active-text)' : 'var(--sidebar-item)',
    background: active ? 'var(--sidebar-active-bg)' : 'transparent',
    borderRadius: 10, marginBottom: 2,
    boxShadow: active ? 'inset 2px 0 0 ' + C.success : 'none',
  }),
  main: { flex: 1, overflowY: 'auto', background: 'var(--main-bg)', padding: 26 },

  card: {
    background: 'var(--surface)',
    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    borderRadius: 18, border: '1px solid var(--card-border)',
    boxShadow: 'var(--card-shadow)',
    padding: 22, marginBottom: 18,
  },
  cardTitle: {
    fontSize: 14.5, fontWeight: 800, color: 'var(--text-strong)',
    marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8,
    letterSpacing: '-0.2px',
  },
  input: {
    border: '1.5px solid var(--card-border)', background: 'var(--input-bg)',
    color: 'var(--text-strong)',
    borderRadius: 11, padding: '10px 13px', fontSize: 13, width: '100%',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  },
  btn: (color = C.green) => ({
    background: `linear-gradient(180deg, ${color} 0%, ${color}E6 100%)`,
    color: '#fff', border: 'none', borderRadius: 11,
    padding: '10px 20px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 6,
    boxShadow: `0 2px 10px -3px ${color}88`,
  }),
  badge: (bg, color) => ({
    background: bg, color, fontSize: 9.5, fontWeight: 700,
    padding: '4px 9px', borderRadius: 999, display: 'inline-flex', alignItems: 'center',
  }),
  stat: {
    background: 'var(--surface)', borderRadius: 18,
    border: '1px solid var(--card-border)', boxShadow: 'var(--card-shadow)',
    padding: 22, textAlign: 'center',
  },
  statVal: {
    fontSize: 30, fontWeight: 800, marginBottom: 4, letterSpacing: '-1px',
    background: `linear-gradient(135deg, ${C.green}, ${C.success})`,
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  statLabel: {
    fontSize: 10.5, color: 'var(--text-mute)', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '.6px',
  },
  info: (type) => {
    const map = {
      green: [C.success, 'rgba(14,138,168,.12)'],
      blue:  [C.blue,    'rgba(47,91,255,.12)'],
      gold:  [C.gold,    'rgba(14,138,168,.12)'],
      red:   [C.red,     'rgba(220,38,38,.12)'],
    };
    const [color, bg] = map[type] || map.green;
    return {
      background: bg, border: `1px solid ${color}44`,
      borderLeft: `3px solid ${color}`,
      color, borderRadius: 12, padding: '11px 15px',
      fontSize: 12, marginBottom: 14, lineHeight: 1.65,
    };
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: {
    textAlign: 'left', padding: '11px 13px', background: 'var(--th-bg)',
    color: 'var(--text-mute)', fontWeight: 700, fontSize: 10,
    textTransform: 'uppercase', letterSpacing: '.6px',
    borderBottom: '1px solid var(--hairline)',
  },
  td: {
    padding: '12px 13px', borderBottom: '1px solid var(--hairline)',
    color: 'var(--text-strong)', verticalAlign: 'middle',
  },
  tdMono: {
    padding: '12px 13px', borderBottom: '1px solid var(--hairline)',
    color: 'var(--text-soft)', verticalAlign: 'middle',
    fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11,
  },
  // auto-fit/minmax plutôt que 1fr 1fr fixe : passe seul en une colonne sur
  // petit écran, sans media query ni logique JS par écran.
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 14 },
  formGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  formLabel: { fontSize: 11.5, fontWeight: 700, color: 'var(--text-strong)' },
  formSub: { fontSize: 10.5, color: 'var(--text-mute)', marginTop: 2, lineHeight: 1.5 },
};

// ─── LOGIN ────────────────────────────────────────────────
function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState(1);
  const [code, setCode] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [devCode, setDevCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const sendOtp = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/api/auth/send-otp', { contact: email, method: 'email' });
      if (data.mfaRequired) {
        // Double authentification déjà activée sur ce compte : l'OTP par
        // e-mail n'a même pas été envoyé, on passe direct au code TOTP.
        setMfaToken(data.mfaToken);
        setStep(3);
      } else {
        if (data.devCode) setDevCode(data.devCode);
        setStep(2);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur envoi OTP');
    } finally { setLoading(false); }
  };

  const finishLogin = (data) => {
    if (data.user.role !== 'admin') throw new Error('Accès réservé aux administrateurs');
    localStorage.setItem('admin_token', data.token);
    localStorage.setItem('admin_user', JSON.stringify(data.user));
    onLogin(data.user);
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/api/auth/verify-otp', { contact: email, code, role: 'admin' });
      if (data.mfaRequired) {
        setMfaToken(data.mfaToken);
        setStep(3);
      } else {
        finishLogin(data);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Code invalide');
    } finally { setLoading(false); }
  };

  const verifyMfa = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/api/auth/mfa/verify-login', { mfaToken, code: mfaCode });
      finishLogin(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Code invalide');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--shell-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--surface-solid)', borderRadius: 22, padding: 40, width: 380, boxShadow: '0 24px 70px -18px rgba(0,0,0,.45)', border: '1px solid var(--card-border)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ width: 56, height: 56, background: C.green, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}><Wrench size={26} color="#FFFFFF" strokeWidth={2.25} /></div>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.dark }}>Mon Dépanneur CI</div>
          <div style={{ fontSize: 12, color: '#8A93AD', marginTop: 4 }}>Dashboard Administrateur</div>
        </div>
        {error && <div style={s.info('red')}>{error}</div>}
        {devCode && <div style={{ ...s.info('gold'), display: 'flex', alignItems: 'center', gap: 6 }}><Ic icon={Key} color={C.gold} size={14} /> Code dev : <strong>{devCode}</strong></div>}
        {step === 1 ? (
          <form onSubmit={sendOtp}>
            <div style={{ marginBottom: 12 }}>
              <label style={s.formLabel}>Email administrateur</label>
              <input style={{ ...s.input, marginTop: 4 }} type="email" value={email}
                     placeholder="ton.email@domaine.com" autoComplete="username"
                     onChange={e => setEmail(e.target.value)} required />
            </div>
            <button style={{ ...s.btn(), width: '100%', justifyContent: 'center', padding: 14, marginTop: 8, fontSize: 14 }} type="submit" disabled={loading}>
              {loading ? <><Loader2 size={14} /> Envoi...</> : <><Mail size={14} /> Recevoir le code</>}
            </button>
          </form>
        ) : step === 2 ? (
          <form onSubmit={verifyOtp}>
            <div style={{ marginBottom: 12 }}>
              <label style={s.formLabel}>Code reçu par email</label>
              <input style={{ ...s.input, marginTop: 4, fontSize: 20, textAlign: 'center', letterSpacing: 8 }}
                     maxLength="6" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} required autoFocus />
            </div>
            <button style={{ ...s.btn(), width: '100%', justifyContent: 'center', padding: 14, fontSize: 14 }} type="submit" disabled={loading}>
              {loading ? <><Loader2 size={14} /> Vérification...</> : <><Lock size={14} /> Se connecter</>}
            </button>
            <button type="button" onClick={() => setStep(1)} style={{ width: '100%', background: 'transparent', border: 'none', color: '#8A93AD', fontSize: 12, marginTop: 12, cursor: 'pointer' }}>
              ← Changer d'email
            </button>
          </form>
        ) : (
          <form onSubmit={verifyMfa}>
            <div style={{ marginBottom: 12 }}>
              <label style={s.formLabel}>Code de double authentification</label>
              <div style={{ fontSize: 11, color: '#8A93AD', marginBottom: 6 }}>
                Depuis ton app d'authentification, ou un code de secours.
              </div>
              <input style={{ ...s.input, marginTop: 4, fontSize: 18, textAlign: 'center', letterSpacing: 4 }}
                     value={mfaCode} onChange={e => setMfaCode(e.target.value)} required autoFocus />
            </div>
            <button style={{ ...s.btn(), width: '100%', justifyContent: 'center', padding: 14, fontSize: 14 }} type="submit" disabled={loading}>
              {loading ? <><Loader2 size={14} /> Vérification...</> : <><Lock size={14} /> Confirmer</>}
            </button>
            <button type="button" onClick={() => setStep(1)} style={{ width: '100%', background: 'transparent', border: 'none', color: '#8A93AD', fontSize: 12, marginTop: 12, cursor: 'pointer' }}>
              ← Changer d'email
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── STATS / ACTIVITÉ TEMPS RÉEL ──────────────────────────
const STATUS_META = {
  pending:   { label: 'Nouvelle',        ico: Sparkles,     color: '#8A93AD' },
  proposed:  { label: 'Devis proposé',   ico: MessageSquare, color: '#7FA6FF' },
  accepted:  { label: 'Acceptée',        ico: Handshake,    color: '#2F5BFF' },
  en_route:  { label: 'En route',        ico: Car,          color: '#1E3FCC' },
  on_site:   { label: 'Sur place',       ico: MapPin,       color: '#0B1F5C' },
  completed: { label: 'Terminée',        ico: Flag,         color: '#0E8AA8' },
  paid:      { label: 'Payée',           ico: DollarSign,   color: '#0B6E85' },
  cancelled: { label: 'Annulée',         ico: Ban,          color: '#DC2626' },
  refused:   { label: 'Refusée',         ico: XCircle,      color: '#DC2626' },
};

// Hook : exécute `fn` au montage puis toutes les `intervalMs` ms.
// Pause automatique si l'onglet est en arrière-plan (économie CPU/réseau).
function useAutoRefresh(fn, intervalMs = 20000, deps = []) {
  useEffect(() => {
    let alive = true;
    const tick = () => { if (alive && !document.hidden) fn(); };
    tick();
    const id = setInterval(tick, intervalMs);
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, deps); // eslint-disable-line
}

function timeAgoFr(iso) {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return 'à l\'instant';
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  return `il y a ${d} j`;
}

function StatsPanel() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [tick, setTick] = useState(0);

  // Auto-refresh toutes les 15 s pour l'effet "temps réel"
  useEffect(() => {
    const fetchAll = () => {
      api.get('/api/admin/stats').then(r => setStats(r.data)).catch(() => setStats({}));
      api.get('/api/admin/activity').then(r => setActivity(r.data)).catch(() => setActivity({}));
    };
    fetchAll();
    const id = setInterval(() => { fetchAll(); setTick(t => t + 1); }, 15000);
    return () => clearInterval(id);
  }, []);

  if (!stats || !activity) return <div style={{ color: '#8A93AD', padding: 20 }}>Chargement…</div>;

  const cards = [
    { label: 'Clients',           val: stats.clients || 0,      ico: User,       color: C.green },
    { label: 'Artisans',          val: stats.artisans || 0,     ico: Wrench,     color: C.green },
    { label: 'Premium',           val: stats.premium || 0,      ico: Star,       color: '#D97706' },
    { label: 'KYC en attente',    val: stats.kycPending || 0,   ico: BadgeCheck, color: C.gold },
    { label: 'Paiements attente', val: stats.subsPending || 0,  ico: CreditCard, color: '#16A34A' },
    { label: 'CA ce mois (FCFA)', val: (stats.monthRevenue || 0).toLocaleString(), ico: DollarSign, color: '#16A34A' },
  ];

  const bd = activity.breakdown || {};
  const inProgressTotal = (bd.pending||0) + (bd.proposed||0) + (bd.accepted||0) + (bd.en_route||0) + (bd.on_site||0);
  const finishedTotal   = (bd.completed||0) + (bd.paid||0);
  const refusedTotal    = (bd.cancelled||0) + (bd.refused||0);

  return (
    <div>
      {/* Pulse temps réel */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
        color: 'var(--text-mute)', fontSize: 12,
      }}>
        <span style={{
          display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
          background: C.green, boxShadow: `0 0 0 ${tick % 2 === 0 ? '4px' : '0px'} ${C.green}33`,
          transition: 'box-shadow 0.6s',
        }} />
        <span>Temps réel · {activity.liveUsers || 0} actif{(activity.liveUsers||0) > 1 ? 's' : ''} (5 min) · MAJ auto 15 s</span>
      </div>

      {/* Cartes existantes */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
        {cards.map((st, i) => (
          <div key={i} style={s.stat}>
            <div style={{ marginBottom: 8 }}><Ic icon={st.ico} color={st.color} size={24} /></div>
            <div style={s.statVal}>{st.val}</div>
            <div style={s.statLabel}>{st.label}</div>
          </div>
        ))}
      </div>

      {/* Synthèse interventions */}
      <div style={{ ...s.card, marginBottom: 16 }}>
        <div style={s.cardTitle}><Ic icon={BarChart3} color={C.green} /> Interventions — synthèse</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
          <SyntheseCard color="#2F5BFF" ico={Settings} label="En cours" val={inProgressTotal} />
          <SyntheseCard color="#0E8AA8" ico={Flag} label="Terminées / payées" val={finishedTotal} />
          <SyntheseCard color="#DC2626" ico={Ban} label="Annulées / refusées" val={refusedTotal} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
          {Object.entries(STATUS_META).map(([k, m]) => (
            <div key={k} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 10px', borderRadius: 10,
              background: '#F5F7FC', border: `1px solid ${m.color}22`,
            }}>
              <Ic icon={m.ico} color={m.color} size={16} />
              <span style={{ flex: 1, fontSize: 12, color: '#4A5470' }}>{m.label}</span>
              <span style={{ fontWeight: 800, color: m.color }}>{bd[k] || 0}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Graphes tendance */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginBottom: 16 }}>
        <ActivityLineChart
          title="Interventions — 30 jours"
          data={activity.daily || []}
          series={[
            { key: 'created',   label: 'Créées',    color: '#2F5BFF' },
            { key: 'completed', label: 'Terminées', color: '#0E8AA8' },
            { key: 'paid',      label: 'Payées',    color: '#0B6E85' },
            { key: 'cancelled', label: 'Annulées',  color: '#DC2626' },
          ]}
        />
        <ActivityLineChart
          title="Inscriptions — 30 jours"
          data={activity.signupsDaily || []}
          series={[
            { key: 'clients',  label: 'Clients',  color: '#2F5BFF' },
            { key: 'artisans', label: 'Artisans', color: '#0E8AA8' },
          ]}
        />
      </div>

      <div style={{ ...s.card, marginBottom: 16 }}>
        <div style={s.cardTitle}><Ic icon={Banknote} color={'#16A34A'} /> Revenus journaliers (interventions payées) — 30 jours</div>
        <RevenueBarChart data={activity.daily || []} />
      </div>

      {/* Top artisans du mois */}
      <div style={{ ...s.card, marginBottom: 16 }}>
        <div style={s.cardTitle}><Ic icon={Trophy} color={'#D97706'} /> Top artisans actifs ce mois</div>
        {(activity.topArtisans || []).length === 0 ? (
          <div style={{ color: 'var(--text-mute)', fontSize: 13, padding: 12 }}>Pas encore d'activité ce mois.</div>
        ) : (
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>#</th><th style={s.th}>Artisan</th>
              <th style={s.th}>Payées</th><th style={s.th}>En cours</th>
              <th style={s.th}>CA mois (FCFA)</th>
            </tr></thead>
            <tbody>
              {(activity.topArtisans || []).map((a, i) => (
                <tr key={a.artisan_id}>
                  <td style={s.td}>{i + 1}</td>
                  <td style={s.td}><strong>{a.artisan_name || '—'}</strong><br />
                    <span style={{ color: 'var(--text-mute)', fontSize: 11 }}>{a.contact}</span></td>
                  <td style={s.td}><strong style={{ color: C.success }}>{a.paid_count}</strong></td>
                  <td style={s.td}>{a.in_progress > 0 ? <strong style={{ color: C.green }}>{a.in_progress}</strong> : '—'}</td>
                  <td style={s.td}><strong>{(a.revenue_month || 0).toLocaleString()}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Activité récente */}
      <div className="md-card" style={s.card}>
        <div style={s.cardTitle}><Ic icon={Zap} color={C.green} /> Activité récente (50 dernières)</div>
        <div style={{ maxHeight: 460, overflowY: 'auto' }}>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Statut</th>
              <th style={s.th}>Client</th>
              <th style={s.th}>Artisan</th>
              <th style={s.th}>Métier</th>
              <th style={s.th}>Montant</th>
              <th style={s.th}>Quand</th>
            </tr></thead>
            <tbody>
              {(activity.recent || []).map(r => {
                const m = STATUS_META[r.status] || { label: r.status, ico: null, color: '#8A93AD' };
                const ts = r.paid_at || r.completed_at || r.updated_at || r.created_at;
                return (
                  <tr key={r.id}>
                    <td style={s.td}>
                      <span style={{ ...s.badge(m.color + '22', m.color), display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Ic icon={m.ico} color={m.color} size={13} />{m.label}
                      </span>
                      {r.urgency && r.urgency !== 'normal' && (
                        <div style={{ fontSize: 10, color: '#DC2626', marginTop: 4 }}>
                          ⚡ {r.urgency.replace('_', ' ')}
                        </div>
                      )}
                    </td>
                    <td style={s.td}>{r.client_name || '—'}<br />
                      <span style={{ color: 'var(--text-mute)', fontSize: 11 }}>{r.client_contact}</span></td>
                    <td style={s.td}>{r.artisan_name || '—'}<br />
                      <span style={{ color: 'var(--text-mute)', fontSize: 11 }}>{r.artisan_contact}</span></td>
                    <td style={s.td}>{r.metier}</td>
                    <td style={s.td}>{r.price ? `${(r.price).toLocaleString()} F` : '—'}</td>
                    <td style={s.td}><span style={{ color: 'var(--text-mute)', fontSize: 12 }}>{timeAgoFr(ts)}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SyntheseCard({ color, ico, label, val }) {
  return (
    <div style={{
      padding: 14, borderRadius: 12,
      background: color + '11', border: `1px solid ${color}44`,
    }}>
      <Ic icon={ico} color={color} size={22} />
      <div style={{ fontSize: 28, fontWeight: 800, color, marginTop: 4 }}>{val}</div>
      <div style={{ fontSize: 11.5, color: '#4A5470' }}>{label}</div>
    </div>
  );
}

// Mini line chart maison (SVG, sans dépendance)
function ActivityLineChart({ title, data, series }) {
  const W = 460, H = 180, P = 28;
  if (!data.length) return (
    <div className="md-card" style={s.card}><div style={s.cardTitle}>{title}</div>
      <div style={{ color: 'var(--text-mute)', fontSize: 13, padding: 12 }}>Pas de données.</div></div>
  );
  const xs = data.map((_, i) => P + i * ((W - 2 * P) / Math.max(data.length - 1, 1)));
  const allVals = series.flatMap(sr => data.map(d => +d[sr.key] || 0));
  const maxY = Math.max(...allVals, 1);
  const yScale = v => H - P - (v / maxY) * (H - 2 * P);

  return (
    <div className="md-card" style={s.card}>
      <div style={s.cardTitle}>{title}</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 180 }}>
        {/* Grille */}
        {[0.25, 0.5, 0.75].map(f => (
          <line key={f} x1={P} x2={W - P} y1={H - P - f * (H - 2 * P)} y2={H - P - f * (H - 2 * P)}
                stroke="#E1E5EF" strokeDasharray="3 3" />
        ))}
        <line x1={P} x2={W - P} y1={H - P} y2={H - P} stroke="#E1E5EF" />
        {/* Lignes */}
        {series.map(sr => {
          const pts = data.map((d, i) => `${xs[i]},${yScale(+d[sr.key] || 0)}`).join(' ');
          return <polyline key={sr.key} points={pts} fill="none" stroke={sr.color} strokeWidth="2" />;
        })}
        {/* Y max */}
        <text x={P} y={P - 6} fontSize="10" fill="#8A93AD">{maxY}</text>
        <text x={P} y={H - 6} fontSize="10" fill="#8A93AD">{data[0]?.day?.slice(5) || ''}</text>
        <text x={W - P - 30} y={H - 6} fontSize="10" fill="#8A93AD">{data[data.length - 1]?.day?.slice(5) || ''}</text>
      </svg>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 11, marginTop: 6 }}>
        {series.map(sr => (
          <div key={sr.key} style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#4A5470' }}>
            <span style={{ display: 'inline-block', width: 10, height: 3, background: sr.color }} />
            {sr.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function RevenueBarChart({ data }) {
  const W = 940, H = 160, P = 32;
  if (!data.length) return <div style={{ color: 'var(--text-mute)' }}>Pas de données.</div>;
  const max = Math.max(...data.map(d => +d.revenue || 0), 1);
  const bw = (W - 2 * P) / data.length - 4;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 160 }}>
      {data.map((d, i) => {
        const v = +d.revenue || 0;
        const h = (v / max) * (H - 2 * P);
        const x = P + i * (bw + 4);
        const y = H - P - h;
        return (
          <g key={d.day}>
            <rect x={x} y={y} width={bw} height={h} rx={2} fill="#0E8AA8">
              <title>{`${d.day}: ${v.toLocaleString()} FCFA · ${d.paid} interv.`}</title>
            </rect>
          </g>
        );
      })}
      <line x1={P} x2={W - P} y1={H - P} y2={H - P} stroke="#E1E5EF" />
      <text x={P} y={P - 6} fontSize="10" fill="#8A93AD">{max.toLocaleString()} F</text>
    </svg>
  );
}

// ─── KYC ──────────────────────────────────────────────────
function KYCPanel() {
  const [artisans, setArtisans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // artisan sélectionné pour détail

  const load = () => api.get('/api/admin/kyc-pending').then(r => { setArtisans(r.data.artisans || []); setLoading(false); });
  useAutoRefresh(load, 20000);

  const validate = async (id, approved) => {
    const notes = approved ? '' : (prompt('Motif du rejet :') || 'Documents non conformes');
    await api.post(`/api/admin/kyc/${id}`, { approved, notes });
    setSelected(null); load();
  };

  const decBadge = (d) => {
    const m = { approve: [C.successL, C.success, '✅ approve'], reject: [C.redL, C.red, '❌ reject'], review: [C.greenL, C.green, '⏳ review'], error: [C.redL, C.red, '⚠️ error'] };
    const [bg, col, lbl] = m[d] || ['#F5F7FC', '#8A93AD', d || '—'];
    return <span style={s.badge(bg, col)}>{lbl}</span>;
  };

  return (
    <div className="md-card" style={s.card}>
      <div style={s.cardTitle}><Ic icon={BadgeCheck} color={C.gold} /> Validations KYC en attente ({artisans.length})</div>
      <div style={s.info('gold')}>⚠️ Vérifiez les documents avant de valider. L'IA peut aider mais l'admin a le dernier mot.</div>
      {loading ? <div>Chargement...</div> :
        artisans.length === 0 ? <div style={s.info('green')}>✅ Aucune validation en attente.</div> :
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Artisan</th><th style={s.th}>Métier</th>
            <th style={s.th}>Docs</th><th style={s.th}>Analyse IA</th><th style={s.th}>Actions</th>
          </tr></thead>
          <tbody>
            {artisans.map(a => (
              <tr key={a.id}>
                <td style={s.td}><strong>{a.name}</strong><br /><span style={{ color: '#8A93AD', fontSize: 11 }}>{a.contact}</span></td>
                <td style={s.td}>{a.metier}</td>
                <td style={s.td}>
                  {a.kyc_doc_url && <a href={a.kyc_doc_url} target="_blank" rel="noreferrer" style={{ color: C.blue, marginRight: 8 }}>📄 Doc</a>}
                  {a.kyc_selfie_url && <a href={a.kyc_selfie_url} target="_blank" rel="noreferrer" style={{ color: C.blue }}>🤳 Selfie</a>}
                </td>
                <td style={s.td}>
                  {a.last_ai ? (
                    <div>
                      {decBadge(a.last_ai.decision)}{' '}
                      <span style={{ fontWeight: 700, color: (a.last_ai.overall_confidence || 0) >= 70 ? C.green : C.gold }}>
                        {a.last_ai.overall_confidence || 0}%
                      </span>
                      <div style={{ fontSize: 10, color: '#8A93AD', marginTop: 4, maxWidth: 280 }}>
                        {a.last_ai.reason || '—'}
                      </div>
                    </div>
                  ) : <span style={{ color: '#8A93AD', fontSize: 11 }}>Pas d'analyse</span>}
                </td>
                <td style={s.td}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button style={{ ...s.btn(C.blue), padding: '6px 10px', fontSize: 11 }} onClick={() => setSelected(a.id)}>🔍 Détails</button>
                    <button style={{ ...s.btn(), padding: '6px 10px', fontSize: 11 }} onClick={() => validate(a.id, true)}>✅</button>
                    <button style={{ ...s.btn(C.red), padding: '6px 10px', fontSize: 11 }} onClick={() => validate(a.id, false)}>❌</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>}

      {selected && <KYCDetailsModal artisanId={selected} onClose={() => setSelected(null)} onValidate={validate} decBadge={decBadge} />}
    </div>
  );
}

function KYCDetailsModal({ artisanId, onClose, onValidate, decBadge }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/api/admin/kyc/${artisanId}/details`).then(r => setData(r.data));
  }, [artisanId]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: 'var(--surface-solid)', borderRadius: 18, padding: 24, maxWidth: 900, width: '100%', maxHeight: '90vh', overflowY: 'auto', border: '1px solid var(--card-border)', boxShadow: 'var(--card-shadow)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>🪪 Détails KYC</div>
          <button style={{ background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer' }} onClick={onClose}>×</button>
        </div>

        {!data ? <div>Chargement...</div> : (
          <>
            <div style={{ fontSize: 13, marginBottom: 16 }}>
              <strong>{data.artisan.name}</strong> · {data.artisan.contact} · {data.artisan.metier}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 20 }}>
              <div>
                <div style={s.formLabel}>📄 Document</div>
                {data.artisan.kyc_doc_url && <img src={data.artisan.kyc_doc_url} alt="doc" style={{ width: '100%', borderRadius: 10, border: '1px solid #E1E5EF', marginTop: 6 }} />}
              </div>
              <div>
                <div style={s.formLabel}>🤳 Selfie</div>
                {data.artisan.kyc_selfie_url && <img src={data.artisan.kyc_selfie_url} alt="selfie" style={{ width: '100%', borderRadius: 10, border: '1px solid #E1E5EF', marginTop: 6 }} />}
              </div>
            </div>

            <div style={s.cardTitle}><Ic icon={Bot} color={C.green} /> Historique des analyses IA ({data.aiAnalyses.length})</div>
            {data.aiAnalyses.length === 0 ? (
              <div style={s.info('gold')}>Aucune analyse IA enregistrée.</div>
            ) : (
              <table style={s.table}>
                <thead><tr>
                  <th style={s.th}>Date</th><th style={s.th}>Décision</th><th style={s.th}>Confiance</th>
                  <th style={s.th}>Doc</th><th style={s.th}>Nom</th><th style={s.th}>Face match</th><th style={s.th}>Raison</th>
                </tr></thead>
                <tbody>
                  {data.aiAnalyses.map(ai => (
                    <tr key={ai.id}>
                      <td style={s.td}>{new Date(ai.created_at).toLocaleString('fr-FR')}</td>
                      <td style={s.td}>{decBadge(ai.decision)}</td>
                      <td style={s.td}><strong>{ai.overall_confidence || 0}%</strong></td>
                      <td style={s.td}>{ai.document_type || '—'}</td>
                      <td style={s.td}>{ai.extracted_name || '—'}</td>
                      <td style={s.td}>{ai.face_match ? `✅ ${ai.face_confidence}%` : '❌'}</td>
                      <td style={s.td}><span style={{ fontSize: 11, color: '#8A93AD' }}>{ai.reason || '—'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button style={s.btn()} onClick={() => onValidate(artisanId, true)}>✅ Valider manuellement</button>
              <button style={s.btn(C.red)} onClick={() => onValidate(artisanId, false)}>❌ Rejeter</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── PAIEMENTS ───────────────────────────────────────────
function PaymentsPanel() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get('/api/admin/subscriptions-pending').then(r => { setSubs(r.data.subscriptions || []); setLoading(false); });
  useAutoRefresh(load, 20000);

  const activate = async (id) => {
    if (!window.confirm('Activer le Premium ?')) return;
    await api.post(`/api/admin/subscriptions/${id}/activate`);
    load();
  };
  const reject = async (id) => {
    if (!window.confirm('Rejeter ce paiement ?')) return;
    await api.post(`/api/admin/subscriptions/${id}/reject`);
    load();
  };

  return (
    <div className="md-card" style={s.card}>
      <div style={s.cardTitle}><Ic icon={CreditCard} color={'#16A34A'} /> Paiements en attente ({subs.length})</div>
      <div style={s.info('gold')}>⚠️ Vérifiez votre Mobile Money, puis activez.</div>
      {loading ? <div>Chargement...</div> :
        subs.length === 0 ? <div style={s.info('green')}>✅ Aucun paiement en attente.</div> :
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Artisan</th><th style={s.th}>Plan</th><th style={s.th}>Montant</th>
            <th style={s.th}>Ref</th><th style={s.th}>Notifié le</th><th style={s.th}>Actions</th>
          </tr></thead>
          <tbody>
            {subs.map(sub => (
              <tr key={sub.id}>
                <td style={s.td}><strong>{sub.name}</strong><br /><span style={{ color: '#8A93AD', fontSize: 11 }}>{sub.contact}</span></td>
                <td style={s.td}><span style={s.badge(C.goldL, C.gold)}>{sub.plan}</span></td>
                <td style={s.td}><strong>{sub.amount?.toLocaleString()} FCFA</strong></td>
                <td className="mono" style={s.tdMono}>{sub.payment_ref}</td>
                <td style={s.td}>{new Date(sub.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                <td style={s.td}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={s.btn()} onClick={() => activate(sub.id)}>✓ Activer</button>
                    <button style={s.btn(C.red)} onClick={() => reject(sub.id)}>✗ Rejeter</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>}
    </div>
  );
}

// ─── ARTISANS ────────────────────────────────────────────
function ArtisansPanel() {
  const [artisans, setArtisans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [dossier, setDossier] = useState(null);

  useAutoRefresh(() => {
    api.get('/api/admin/artisans').then(r => { setArtisans(r.data.artisans || []); setLoading(false); });
  }, 30000);

  const kycBadge = (status) => {
    const map = {
      verified: [C.successL, C.success, '✅ Vérifié'],
      pending:  [C.greenL, C.green, '⏳ En attente'],
      rejected: [C.redL, C.red, '❌ Rejeté'],
      none:     ['#F5F7FC', '#8A93AD', '— Aucun'],
    };
    const [bg, color, label] = map[status] || map.none;
    return <span style={s.badge(bg, color)}>{label}</span>;
  };

  const filtered = artisans.filter(a =>
    !filter || (a.name || '').toLowerCase().includes(filter.toLowerCase())
    || (a.metier || '').toLowerCase().includes(filter.toLowerCase())
    || (a.contact || '').includes(filter)
  );

  return (
    <div className="md-card" style={s.card}>
      <div style={s.cardTitle}><Ic icon={Wrench} color={C.green} /> Tous les artisans ({filtered.length})</div>
      <div style={s.info('blue')}>
        💡 Clique sur un nom pour ouvrir son dossier : documents KYC, téléphone
        utilisé et historique de connexion.
      </div>
      <input style={{ ...s.input, marginBottom: 12 }} placeholder="🔍 Filtrer par nom, métier, téléphone..." value={filter} onChange={e => setFilter(e.target.value)} />
      {loading ? <div>Chargement...</div> :
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Nom</th><th style={s.th}>Métier</th><th style={s.th}>Contact</th>
            <th style={s.th}>KYC</th><th style={s.th}>Premium</th><th style={s.th}>Note</th><th style={s.th}>Inscrit le</th>
          </tr></thead>
          <tbody>
            {filtered.map(a => (
              <tr key={a.id}>
                <td style={s.td}>
                  <button onClick={() => setDossier(a.user_id || a.id)}
                          style={{ background: 'none', border: 'none', padding: 0,
                                   cursor: 'pointer', font: 'inherit', fontWeight: 700,
                                   color: C.success, textDecoration: 'underline',
                                   textUnderlineOffset: 3 }}>
                    {a.name || '—'}
                  </button>
                </td>
                <td style={s.td}>{a.metier}</td>
                <td className="mono" style={s.tdMono}>{a.contact}</td>
                <td style={s.td}>{kycBadge(a.kyc_status)}</td>
                <td style={s.td}>{a.is_premium ? <span style={s.badge(C.premiumL, C.premium)}>⭐ Premium</span> : <span style={s.badge('#F5F7FC', '#8A93AD')}>Gratuit</span>}</td>
                <td style={s.td}>{a.rating_avg > 0 ? `⭐ ${a.rating_avg}` : '—'}</td>
                <td style={s.td}>{new Date(a.created_at).toLocaleDateString('fr-FR')}</td>
              </tr>
            ))}
          </tbody>
        </table>}

      {dossier && <UserDossierModal userId={dossier} onClose={() => setDossier(null)} />}
    </div>
  );
}

// ─── DOSSIER UTILISATEUR (KYC + appareil + connexions) ───
function UserDossierModal({ userId, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('kyc');
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    api.get(`/api/admin/user/${userId}/dossier`)
      .then(r => setD(r.data))
      .catch(e => setErr(e.response?.data?.error || 'Chargement impossible'));
  }, [userId]);

  const fmt = (x) => x ? new Date(x).toLocaleString('fr-FR') : '—';
  const fmtDay = (x) => x ? new Date(x).toLocaleDateString('fr-FR') : '—';

  const kycBadge = (st) => {
    const map = {
      verified: [C.success, 'Vérifié'], pending: [C.green, 'En attente'],
      rejected: [C.red, 'Rejeté'],
    };
    const [color, label] = map[st] || ['#8A93AD', 'Non soumis'];
    return <span style={s.badge(color + '22', color)}>{label}</span>;
  };

  const u = d?.user, p = d?.profile;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.62)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 900, padding: 24, backdropFilter: 'blur(3px)',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--surface-solid)', borderRadius: 20, padding: 24,
        maxWidth: 980, width: '100%', maxHeight: '92vh', overflowY: 'auto',
        border: '1px solid var(--card-border)', boxShadow: 'var(--card-shadow)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ ...s.cardTitle, marginBottom: 0, flex: 1 }}>
            <Ic icon={User} color={C.green} /> {u?.name || 'Dossier utilisateur'}
          </div>
          <button style={s.btn('#4A5470')} onClick={onClose}>Fermer</button>
        </div>

        {err && <div style={s.info('red')}>❌ {err}</div>}
        {!d && !err && <div style={s.info('blue')}>⏳ Chargement du dossier…</div>}

        {d && (
          <>
            {/* Identité */}
            <div style={{ display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))',
                          gap: 12, marginBottom: 16 }}>
              <Info label="Contact" value={u.contact} mono />
              <Info label="Rôle" value={u.role} />
              <Info label="Commune" value={u.commune || '—'} />
              <Info label="Inscrit le" value={fmtDay(u.created_at)} />
              {p && <Info label="Métier" value={p.metier} />}
              {p && <Info label="Note" value={p.review_count > 0
                ? `${p.rating_avg} ⭐ (${p.review_count})` : 'Pas d\'avis'} />}
            </div>

            {u.blocked_until && new Date(u.blocked_until) > new Date() && (
              <div style={s.info('red')}>
                🚫 Compte bloqué jusqu'au {fmt(u.blocked_until)}
                {u.blocked_reason ? ` — ${u.blocked_reason}` : ''}
              </div>
            )}

            {d.sharedDeviceAccounts?.length > 0 && (
              <div style={s.info('red')}>
                🚨 <strong>{d.sharedDeviceAccounts.length} autre(s) compte(s)</strong> se
                connectent depuis le même téléphone :{' '}
                {d.sharedDeviceAccounts.map(a => a.name || a.contact).join(', ')}.
                Vérifie s'il s'agit d'un partage légitime ou de comptes multiples.
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, margin: '14px 0', flexWrap: 'wrap' }}>
              {[
                ['kyc', '🪪 KYC'],
                ['device', '📱 Appareil'],
                ['logins', `🕘 Connexions (${d.logins.length})`],
                ['activity', '📊 Activité'],
              ].map(([id, label]) => (
                <button key={id} onClick={() => setTab(id)}
                  style={{ ...s.btn(tab === id ? C.green : '#4A5470'),
                           opacity: tab === id ? 1 : .65, fontSize: 12 }}>
                  {label}
                </button>
              ))}
            </div>

            {/* KYC */}
            {tab === 'kyc' && (
              <div>
                {!p ? (
                  <div style={s.info('blue')}>Cet utilisateur n'a pas de profil artisan.</div>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>
                        Statut :
                      </span>
                      {kycBadge(p.kyc_status)}
                    </div>

                    {p.kyc_notes && (
                      <div style={s.info(p.kyc_status === 'rejected' ? 'red' : 'blue')}>
                        📝 {p.kyc_notes}
                      </div>
                    )}

                    {(p.kyc_doc_url || p.kyc_selfie_url) ? (
                      <div style={{ display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))',
                                    gap: 14 }}>
                        {[['Pièce d\'identité', p.kyc_doc_url],
                          ['Selfie avec le document', p.kyc_selfie_url]]
                          .filter(([, url]) => url)
                          .map(([label, url]) => (
                            <div key={label} style={{
                              border: '1px solid var(--card-border)', borderRadius: 14,
                              overflow: 'hidden', background: 'var(--th-bg)',
                            }}>
                              <img src={url} alt={label} onClick={() => setZoom(url)}
                                   style={{ width: '100%', height: 220, objectFit: 'cover',
                                            cursor: 'zoom-in', display: 'block' }} />
                              <div style={{ padding: 10, fontSize: 12, fontWeight: 700,
                                            color: 'var(--text-strong)' }}>
                                {label}
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div style={s.info('gold')}>
                        Aucun document soumis pour l'instant.
                      </div>
                    )}

                    {d.kycAi?.length > 0 && (
                      <div style={{ marginTop: 18 }}>
                        <div style={s.cardTitle}><Ic icon={Bot} color={C.green} /> Analyse automatique</div>
                        <table style={s.table}>
                          <thead><tr>
                            <th style={s.th}>Date</th><th style={s.th}>Décision</th>
                            <th style={s.th}>Confiance</th><th style={s.th}>Nom extrait</th>
                            <th style={s.th}>Visage</th><th style={s.th}>Motif</th>
                          </tr></thead>
                          <tbody>
                            {d.kycAi.map((a, i) => (
                              <tr key={i}>
                                <td style={s.td}>{fmt(a.created_at)}</td>
                                <td style={s.td}>{a.decision}</td>
                                <td style={s.td}>{a.overall_confidence ?? '—'}%</td>
                                <td style={s.td}>{a.extracted_name || '—'}</td>
                                <td style={s.td}>{a.face_match ? '✅' : '❌'}</td>
                                <td style={s.td}>{a.reason || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Appareil */}
            {tab === 'device' && (
              <div style={{ display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12 }}>
                <Info label="Marque" value={u.device_brand || 'Non renseignée'} />
                <Info label="Modèle" value={u.device_model || 'Non renseigné'} />
                <Info label="Système" value={u.os_version || '—'} />
                <Info label="Version de l'app" value={u.app_version || '—'} />
                <Info label="Plateforme" value={u.platform || '—'} />
                <Info label="Identifiant appareil" value={u.device_id || '—'} mono />
                <Info label="Dernière connexion" value={fmt(u.last_login_at)} />
                <Info label="Dernière IP" value={u.last_login_ip || '—'} mono />
                <Info label="Biométrie" value={u.has_biometric ? 'Activée' : 'Non'} />
              </div>
            )}

            {/* Connexions */}
            {tab === 'logins' && (
              d.logins.length === 0
                ? <div style={s.info('blue')}>
                    Aucune connexion enregistrée. L'historique démarre après la mise à jour
                    de l'application — les connexions antérieures ne sont pas rétroactives.
                  </div>
                : <table style={s.table}>
                    <thead><tr>
                      <th style={s.th}>Date</th><th style={s.th}>Méthode</th>
                      <th style={s.th}>Appareil</th><th style={s.th}>Système</th>
                      <th style={s.th}>App</th><th style={s.th}>IP</th>
                    </tr></thead>
                    <tbody>
                      {d.logins.map((l, i) => (
                        <tr key={i}>
                          <td style={s.td}>{fmt(l.created_at)}</td>
                          <td style={s.td}>
                            <span style={s.badge(
                              l.method === 'biometric' ? C.blueL : C.greenL,
                              l.method === 'biometric' ? C.blue : C.green,
                            )}>
                              {l.method === 'biometric' ? '👆 Empreinte' : '🔑 Code'}
                            </span>
                          </td>
                          <td style={s.td}>
                            {[l.device_brand, l.device_model].filter(Boolean).join(' ') || '—'}
                          </td>
                          <td style={s.td}>{l.os_version || '—'}</td>
                          <td style={s.td}>{l.app_version || '—'}</td>
                          <td className="mono" style={s.tdMono}>{l.ip || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
            )}

            {/* Activité */}
            {tab === 'activity' && (
              <>
                <div style={{ display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
                              gap: 12, marginBottom: 16 }}>
                  <Info label="Interventions" value={d.activity.total} />
                  <Info label="Payées" value={d.activity.paid} />
                  <Info label="Annulées" value={d.activity.cancelled} />
                  <Info label="Refusées" value={d.activity.refused} />
                  <Info label="Montant encaissé"
                        value={`${new Intl.NumberFormat('fr-FR').format(d.activity.revenue)} F`} />
                </div>

                {p && (
                  <div style={{ display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
                                gap: 12 }}>
                    <Info label="Score de fiabilité"
                          value={p.reliability_score != null
                            ? `${p.reliability_score}/100` : 'Pas assez de courses'} />
                    <Info label="Offres reçues" value={p.offers_received ?? 0} />
                    <Info label="Offres acceptées" value={p.offers_accepted ?? 0} />
                    <Info label="Délai de réponse moyen"
                          value={p.avg_response_sec != null
                            ? `${Math.round(p.avg_response_sec / 60)} min` : '—'} />
                    <Info label="Disponible" value={p.is_available ? 'Oui' : 'Non'} />
                    <Info label="Position partagée"
                          value={p.location_enabled ? `Oui · ${p.service_radius_km || 7} km` : 'Non'} />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {zoom && (
        <div onClick={(e) => { e.stopPropagation(); setZoom(null); }}
             style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.92)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 1000, cursor: 'zoom-out', padding: 30 }}>
          <img src={zoom} alt="agrandissement"
               style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 10 }} />
        </div>
      )}
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div style={{ background: 'var(--th-bg)', border: '1px solid var(--card-border)',
                  borderRadius: 12, padding: 12 }}>
      <div style={{ fontSize: 10, color: 'var(--text-mute)', fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>
        {label}
      </div>
      <div className={mono ? 'mono' : undefined}
           style={{ fontSize: mono ? 11.5 : 13, fontWeight: 600, color: 'var(--text-strong)',
                    wordBreak: 'break-all' }}>
        {value}
      </div>
    </div>
  );
}

// ─── REVENUS ARTISANS (CA généré par la plateforme) ──────
function RevenuePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [sortBy, setSortBy] = useState('total'); // total | month | year | count

  useAutoRefresh(() => {
    api.get('/api/admin/revenue')
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, 30000);

  const fmt = (n) => {
    const v = Number(n) || 0;
    return new Intl.NumberFormat('fr-FR').format(v) + ' FCFA';
  };
  const monthLabel = (ym) => {
    const labels = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Aoû','Sep','Oct','Nov','Déc'];
    const m = parseInt((ym || '').split('-')[1]) - 1;
    return labels[m] || ym;
  };

  if (loading) return <div className="md-card" style={s.card}>Chargement…</div>;
  if (!data) return <div className="md-card" style={s.card}>Erreur de chargement</div>;

  const t = data.totals || {};
  const monthly = data.monthly || [];
  const maxMonth = Math.max(1, ...monthly.map(m => Number(m.revenue) || 0));

  const sorted = [...(data.byArtisan || [])].sort((a, b) => {
    switch (sortBy) {
      case 'month': return Number(b.revenue_month) - Number(a.revenue_month);
      case 'year':  return Number(b.revenue_year)  - Number(a.revenue_year);
      case 'count': return Number(b.count_total)   - Number(a.count_total);
      default:      return Number(b.revenue_total) - Number(a.revenue_total);
    }
  });
  const filtered = sorted.filter(a =>
    !filter
    || (a.artisan_name || '').toLowerCase().includes(filter.toLowerCase())
    || (a.metier || '').toLowerCase().includes(filter.toLowerCase())
    || (a.artisan_contact || '').includes(filter)
  );
  const totalArtisansActifs = (data.byArtisan || []).filter(a => Number(a.count_total) > 0).length;

  return (
    <div>
      {/* Hero stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div style={s.stat}>
          <div style={s.statVal}>{fmt(t.revenue_month)}</div>
          <div style={s.statLabel}>CA ce mois ({Number(t.count_month) || 0} interv.)</div>
        </div>
        <div style={s.stat}>
          <div style={s.statVal}>{fmt(t.revenue_year)}</div>
          <div style={s.statLabel}>CA cette année ({Number(t.count_year) || 0} interv.)</div>
        </div>
        <div style={s.stat}>
          <div style={s.statVal}>{fmt(t.revenue_total)}</div>
          <div style={s.statLabel}>CA cumulé ({Number(t.count_total) || 0} interv.)</div>
        </div>
        <div style={s.stat}>
          <div style={{ ...s.statVal, color: C.gold }}>{fmt(t.revenue_pending)}</div>
          <div style={s.statLabel}>En attente paiement ({Number(t.count_pending) || 0})</div>
        </div>
      </div>

      {/* Histogramme 12 mois */}
      <div className="md-card" style={s.card}>
        <div style={s.cardTitle}><Ic icon={TrendingUp} color={'#16A34A'} /> Chiffre d'affaires plateforme — 12 derniers mois</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 160, padding: '8px 4px' }}>
          {monthly.map((m, i) => {
            const v = Number(m.revenue) || 0;
            const h = Math.max(2, Math.round((v / maxMonth) * 140));
            return (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ fontSize: 9, color: '#8A93AD' }}>{v > 0 ? Math.round(v / 1000) + 'k' : ''}</div>
                <div title={`${m.month} : ${fmt(v)} (${m.count} interv.)`}
                     style={{ width: '100%', height: h, background: v > 0 ? C.green : '#E1E5EF',
                              borderRadius: '6px 6px 0 0' }} />
                <div style={{ fontSize: 10, color: '#8A93AD' }}>{monthLabel(m.month)}</div>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: '#8A93AD', marginTop: 8 }}>
          Cumul reversé aux artisans sur cette période :
          <strong style={{ color: C.green, marginLeft: 4 }}>
            {fmt(monthly.reduce((s, m) => s + (Number(m.revenue) || 0), 0))}
          </strong>
          {' · '}
          {monthly.reduce((s, m) => s + (Number(m.count) || 0), 0)} interventions payées
          {' · '}
          {totalArtisansActifs} artisan{totalArtisansActifs > 1 ? 's' : ''} actif{totalArtisansActifs > 1 ? 's' : ''}
        </div>
      </div>

      {/* Tableau par artisan */}
      <div className="md-card" style={s.card}>
        <div style={s.cardTitle}><Ic icon={User} color={C.green} /> Chiffre d'affaires par artisan ({filtered.length})</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <input style={{ ...s.input, flex: 1, minWidth: 220 }}
                 placeholder="🔍 Filtrer par nom, métier, contact…"
                 value={filter} onChange={e => setFilter(e.target.value)} />
          {[
            ['total', 'Cumul'],
            ['month', 'Ce mois'],
            ['year',  'Cette année'],
            ['count', '# interv.'],
          ].map(([id, label]) => (
            <button key={id}
                    style={{ ...s.btn(sortBy === id ? C.green : '#8A93AD'), fontSize: 11, padding: '8px 14px' }}
                    onClick={() => setSortBy(id)}>
              Trier : {label}
            </button>
          ))}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>#</th>
              <th style={s.th}>Artisan</th>
              <th style={s.th}>Métier</th>
              <th style={s.th}>Statut</th>
              <th style={{ ...s.th, textAlign: 'right' }}>Ce mois</th>
              <th style={{ ...s.th, textAlign: 'right' }}>Cette année</th>
              <th style={{ ...s.th, textAlign: 'right' }}>Cumul</th>
              <th style={{ ...s.th, textAlign: 'right' }}>Interv. payées</th>
              <th style={s.th}>Dernier paiement</th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td style={s.td} colSpan={9}>
                  <em style={{ color: '#8A93AD' }}>Aucun artisan ne correspond.</em>
                </td></tr>
              )}
              {filtered.map((a, idx) => (
                <tr key={a.artisan_id}>
                  <td className="mono" style={s.tdMono}>{idx + 1}</td>
                  <td style={s.td}>
                    <strong>{a.artisan_name || '—'}</strong>
                    <div style={{ fontSize: 10, color: '#8A93AD', fontFamily: 'monospace' }}>
                      {a.artisan_contact}
                    </div>
                  </td>
                  <td style={s.td}>{a.metier}</td>
                  <td style={s.td}>
                    {a.is_premium
                      ? <span style={s.badge(C.premiumL, C.premium)}>⭐ Premium</span>
                      : <span style={s.badge('#F5F7FC', '#8A93AD')}>Gratuit</span>}
                  </td>
                  <td style={{ ...s.td, textAlign: 'right' }}>
                    {Number(a.revenue_month) > 0
                      ? <strong style={{ color: C.green }}>{fmt(a.revenue_month)}</strong>
                      : <span style={{ color: '#8A93AD' }}>—</span>}
                    <div style={{ fontSize: 10, color: '#8A93AD' }}>
                      {Number(a.count_month) || 0} interv.
                    </div>
                  </td>
                  <td style={{ ...s.td, textAlign: 'right' }}>
                    {fmt(a.revenue_year)}
                    <div style={{ fontSize: 10, color: '#8A93AD' }}>
                      {Number(a.count_year) || 0} interv.
                    </div>
                  </td>
                  <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: C.greenD }}>
                    {fmt(a.revenue_total)}
                  </td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{Number(a.count_total) || 0}</td>
                  <td style={s.td}>
                    {a.last_paid_at
                      ? new Date(a.last_paid_at).toLocaleDateString('fr-FR')
                      : <span style={{ color: '#8A93AD' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── ÉQUIPE STAFF (super-admin only) ─────────────────────
const STAFF_ROLES = [
  { id: 'admin',     label: 'Administrateur',     ico: Crown,     desc: 'Accès total à tout le dashboard',           color: C.green },
  { id: 'comptable', label: 'Comptable',          ico: Calculator, desc: 'Stats, paiements premium, revenus artisans', color: '#0B6E85' },
  { id: 'support',   label: 'Support client',     ico: LifeBuoy,  desc: 'Signalements, utilisateurs, modération',     color: C.blue },
  { id: 'suivi',     label: 'Suivi interventions', ico: Search,   desc: 'KYC, artisans, bannière',                    color: C.gold },
];
const roleMeta = (id) => STAFF_ROLES.find(r => r.id === id) || { id, label: id, ico: User, color: '#8A93AD', desc: '' };

function MetiersPanel() {
  const [metiers, setMetiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ label: '', emoji: '🔨' });

  const load = () => {
    setLoading(true);
    api.get('/api/admin/metiers')
      .then(r => { setMetiers(r.data.metiers || []); setError(''); })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true); setError('');
    try {
      const slug = form.label.trim().toLowerCase()
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      await api.post('/api/admin/metiers', { id: slug, label: form.label.trim(), emoji: form.emoji.trim() || '🔨' });
      setForm({ label: '', emoji: '🔨' });
      load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setCreating(false); }
  };

  const updateField = async (id, patch) => {
    try { await api.put(`/api/admin/metiers/${id}`, patch); load(); }
    catch (err) { alert(err.response?.data?.error || err.message); }
  };

  return (
    <div>
      <div className="md-card" style={s.card}>
        <div style={s.cardTitle}><Ic icon={Plus} color={C.green} /> Ajouter un métier</div>
        <div style={s.info('blue')}>
          Le métier apparaît immédiatement dans l'app (recherche client, inscription
          artisan) — aucune mise à jour de l'app n'est nécessaire.
        </div>
        <form onSubmit={submit} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ ...s.formGroup, width: 70 }}>
            <label style={{ fontSize: 11, color: '#8A93AD' }}>Émoji</label>
            <input style={{ ...s.input, textAlign: 'center', fontSize: 18 }} maxLength={4}
                   value={form.emoji} onChange={e => setForm({ ...form, emoji: e.target.value })} />
          </div>
          <div style={{ ...s.formGroup, flex: '1 1 200px' }}>
            <label style={{ fontSize: 11, color: '#8A93AD' }}>Nom du métier</label>
            <input style={s.input} required placeholder="ex : Vulcanisateur"
                   value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} />
          </div>
          <button type="submit" style={{ ...s.btn(), padding: '10px 18px' }} disabled={creating || !form.label.trim()}>
            {creating ? '…' : 'Ajouter'}
          </button>
        </form>
        {error && <div style={{ ...s.info('red'), marginTop: 12 }}>{error}</div>}
      </div>

      <div className="md-card" style={s.card}>
        <div style={s.cardTitle}><Ic icon={Hammer} color={C.gold} /> Métiers ({metiers.length})</div>
        {loading ? <div>Chargement…</div> : (
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}></th>
              <th style={s.th}>Métier</th>
              <th style={s.th}>Identifiant</th>
              <th style={s.th}>Statut</th>
              <th style={s.th}></th>
            </tr></thead>
            <tbody>
              {metiers.map(m => (
                <tr key={m.id} style={{ opacity: m.active ? 1 : 0.5 }}>
                  <td style={s.td}>
                    <input
                      style={{ width: 40, textAlign: 'center', fontSize: 16, border: '1px solid #E1E5EF', borderRadius: 6 }}
                      defaultValue={m.emoji}
                      onBlur={e => { if (e.target.value !== m.emoji) updateField(m.id, { emoji: e.target.value }); }}
                    />
                  </td>
                  <td style={s.td}>
                    <input
                      style={{ ...s.input, padding: '6px 8px' }}
                      defaultValue={m.label}
                      onBlur={e => {
                        const v = e.target.value.trim();
                        if (v && v !== m.label) updateField(m.id, { label: v });
                      }}
                    />
                  </td>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 11, color: '#8A93AD' }}>{m.id}</td>
                  <td style={s.td}>
                    <span style={s.badge(m.active ? C.successL : C.redL, m.active ? C.success : C.red)}>
                      {m.active ? 'Actif' : 'Désactivé'}
                    </span>
                  </td>
                  <td style={s.td}>
                    <button
                      style={{ ...s.btn(m.active ? C.red : C.green), padding: '6px 12px', fontSize: 11 }}
                      onClick={() => updateField(m.id, { active: !m.active })}
                    >
                      {m.active ? 'Désactiver' : 'Réactiver'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ ...s.formSub, marginTop: 12 }}>
          💡 Un métier n'est jamais supprimé (les interventions passées le
          référencent) — seulement désactivé : il disparaît de la recherche et de
          l'inscription artisan sans casser l'historique.
        </div>
      </div>
    </div>
  );
}

const EXPENSE_CATEGORIES = [
  { id: 'personnel',           label: 'Personnel (salaires, charges sociales)' },
  { id: 'loyers_charges',      label: 'Loyers & charges locatives' },
  { id: 'achats_consommes',    label: 'Achats consommés' },
  { id: 'services_exterieurs', label: 'Services extérieurs (prestataires, hébergement...)' },
  { id: 'impots_taxes',        label: 'Impôts & taxes' },
  { id: 'autres_charges',      label: 'Autres charges' },
];

/** Taux de croissance mensuels observés (CA) sur les données disponibles. */
function monthlyGrowthRates(monthly) {
  const vals = monthly.map(m => Number(m.ca) || 0);
  const rates = [];
  for (let i = 1; i < vals.length; i++) {
    if (vals[i - 1] > 0) rates.push((vals[i] - vals[i - 1]) / vals[i - 1]);
  }
  return rates;
}

function nextMonths(fromMonth, count) {
  let [y, m] = (fromMonth || '').split('-').map(Number);
  const out = [];
  for (let i = 0; i < count; i++) {
    m += 1; if (m > 12) { m = 1; y += 1; }
    out.push(`${y}-${String(m).padStart(2, '0')}`);
  }
  return out;
}

/** Projection indicative : croissance moyenne des 6 derniers mois, extrapolée. */
function projectCA(monthly, monthsAhead = 3) {
  const recent = monthly.slice(-6).map(m => Number(m.ca) || 0);
  if (recent.length < 2) return [];
  const rates = monthlyGrowthRates(monthly.slice(-6));
  const avgGrowth = rates.length ? rates.reduce((a, b) => a + b, 0) / rates.length : 0;
  let last = recent[recent.length - 1] || 0;
  return nextMonths(monthly[monthly.length - 1]?.month, monthsAhead).map(month => {
    last = Math.max(0, last * (1 + avgGrowth));
    return { month, ca: Math.round(last), projected: true };
  });
}

/**
 * Trois scénarios de projection (pessimiste / réaliste / optimiste), basés
 * respectivement sur le taux de croissance mensuel le plus bas, moyen et le
 * plus haut observés sur les 6 derniers mois. Indicatif — pas une prévision
 * financière engageante.
 */
function projectScenarios(monthly, monthsAhead = 6) {
  const rates = monthlyGrowthRates(monthly.slice(-6));
  const last = Number(monthly[monthly.length - 1]?.ca) || 0;
  const fromMonth = monthly[monthly.length - 1]?.month;
  if (!rates.length || !last) {
    return { pessimiste: [], realiste: [], optimiste: [] };
  }
  const avg = rates.reduce((a, b) => a + b, 0) / rates.length;
  const min = Math.min(...rates);
  const max = Math.max(...rates);
  const build = (rate) => {
    let v = last;
    return nextMonths(fromMonth, monthsAhead).map(month => {
      v = Math.max(0, v * (1 + rate));
      return { month, ca: Math.round(v) };
    });
  };
  return { pessimiste: build(min), realiste: build(avg), optimiste: build(max) };
}

/** Génère et déclenche le téléchargement d'un CSV à partir d'un tableau d'objets. */
function downloadCSV(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.join(';'), ...rows.map(r => headers.map(h => escape(r[h])).join(';'))].join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function ComptabilitePanel() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('ca'); // ca | charges | resultat | fiscal

  useAutoRefresh(() => {
    api.get('/api/admin/accounting/summary')
      .then(r => { setSummary(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, 30000);

  const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Number(n) || 0) + ' FCFA';
  const monthLabel = (ym) => {
    const labels = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Aoû','Sep','Oct','Nov','Déc'];
    const m = parseInt((ym || '').split('-')[1]) - 1;
    return labels[m] || ym;
  };

  const TABS = [
    { id: 'ca',       label: 'CA & projections',    ico: TrendingUp },
    { id: 'charges',  label: 'Charges',              ico: Banknote },
    { id: 'resultat', label: 'Résultat',             ico: BarChart3 },
    { id: 'fiscal',   label: 'Déclarations fiscales', ico: FileText },
  ];

  return (
    <div>
      <div style={s.info('gold')}>
        <strong>Outil de suivi interne</strong> — pas un outil de déclaration fiscale
        officielle. Les taux, montants et échéances (onglet « Déclarations fiscales »)
        sont éditables et doivent être vérifiés avec un expert-comptable agréé avant
        toute utilisation réelle auprès de la DGI.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              ...s.btn(tab === t.id ? C.green : '#8A93AD'),
              opacity: tab === t.id ? 1 : 0.55,
              padding: '9px 16px', fontSize: 12,
            }}
          >
            <Ic icon={t.ico} color="#fff" size={14} /> {t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="md-card" style={s.card}>Chargement…</div> : !summary ? (
        <div className="md-card" style={s.card}>Erreur de chargement</div>
      ) : (
        <>
          {tab === 'ca' && <CaTab summary={summary} fmt={fmt} monthLabel={monthLabel} />}
          {tab === 'charges' && <ChargesTab />}
          {tab === 'resultat' && <ResultatTab summary={summary} fmt={fmt} monthLabel={monthLabel} />}
          {tab === 'fiscal' && <FiscalTab />}
        </>
      )}
    </div>
  );
}

function CaTab({ summary, fmt, monthLabel }) {
  const monthly = summary.monthly || [];
  const projection = projectCA(monthly, 3);
  const combined = [...monthly, ...projection];
  const maxVal = Math.max(1, ...combined.map(m => Number(m.ca) || 0));

  const lastCa = Number(monthly[monthly.length - 1]?.ca) || 0;
  const prevCa = Number(monthly[monthly.length - 2]?.ca) || 0;
  const momGrowth = prevCa > 0 ? ((lastCa - prevCa) / prevCa) * 100 : null;
  const marge = summary.ca.month > 0 ? (summary.resultatNet.month / summary.ca.month) * 100 : null;

  const scenarios = projectScenarios(monthly, 6);
  const scenarioMonths = scenarios.realiste.map(s => s.month);

  const exportCsv = () => downloadCSV('chiffre-affaires.csv', monthly.map(m => ({
    mois: m.month, ca_fcfa: m.ca, charges_fcfa: m.charges, resultat_fcfa: m.resultat,
  })));

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div style={s.stat}>
          <div style={s.statVal}>{fmt(summary.ca.month)}</div>
          <div style={s.statLabel}>CA ce mois (abonnements)</div>
        </div>
        <div style={s.stat}>
          <div style={s.statVal}>{fmt(summary.ca.year)}</div>
          <div style={s.statLabel}>CA cette année</div>
        </div>
        <div style={s.stat}>
          <div style={{ ...s.statVal, color: marge == null ? '#8A93AD' : marge >= 0 ? C.success : C.red }}>
            {marge == null ? '—' : marge.toFixed(1) + ' %'}
          </div>
          <div style={s.statLabel}>Marge nette (mois)</div>
        </div>
        <div style={s.stat}>
          <div style={{ ...s.statVal, color: momGrowth == null ? '#8A93AD' : momGrowth >= 0 ? C.success : C.red }}>
            {momGrowth == null ? '—' : (momGrowth >= 0 ? '+' : '') + momGrowth.toFixed(1) + ' %'}
          </div>
          <div style={s.statLabel}>Croissance vs mois précédent</div>
        </div>
        <div style={s.stat}>
          <div style={{ ...s.statVal, color: '#8A93AD' }}>{fmt(summary.interventionVolume.month)}</div>
          <div style={s.statLabel}>Volume interventions (hors CA)</div>
        </div>
      </div>

      <div style={s.info('blue')}>{summary.interventionVolume.note}</div>

      <div className="md-card" style={s.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={s.cardTitle}><Ic icon={TrendingUp} color={C.green} /> Chiffre d'affaires — 12 derniers mois + projection 3 mois</div>
          <button style={{ ...s.btn('#8A93AD'), padding: '7px 14px', fontSize: 11 }} onClick={exportCsv}>
            <Ic icon={FileText} color="#fff" size={12} /> Exporter CSV
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 160, padding: '8px 4px' }}>
          {combined.map((m, i) => {
            const v = Number(m.ca) || 0;
            const h = Math.max(2, Math.round((v / maxVal) * 140));
            return (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ fontSize: 9, color: '#8A93AD' }}>{v > 0 ? Math.round(v / 1000) + 'k' : ''}</div>
                <div title={`${m.month} : ${fmt(v)}${m.projected ? ' (projection)' : ''}`}
                     style={{
                       width: '100%', height: h,
                       background: m.projected ? C.green + '55' : C.green,
                       border: m.projected ? `1px dashed ${C.green}` : 'none',
                       borderRadius: '6px 6px 0 0',
                     }} />
                <div style={{ fontSize: 10, color: m.projected ? C.green : '#8A93AD', fontWeight: m.projected ? 700 : 400 }}>
                  {monthLabel(m.month)}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ ...s.formSub, marginTop: 8 }}>
          Barres hachurées = projection indicative, basée sur la croissance moyenne
          des 6 derniers mois. N'engage à rien — juste une estimation de tendance.
        </div>
      </div>

      <div className="md-card" style={s.card}>
        <div style={s.cardTitle}><Ic icon={Zap} color={C.green} /> Projection à 6 mois — 3 scénarios</div>
        {!scenarioMonths.length ? (
          <div style={{ color: 'var(--text-mute)', fontSize: 13 }}>
            Pas assez d'historique pour projeter (au moins 2 mois de CA nécessaires).
          </div>
        ) : (
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Scénario</th>
              {scenarioMonths.map(m => <th key={m} style={s.th}>{monthLabel(m)}</th>)}
            </tr></thead>
            <tbody>
              <tr>
                <td style={{ ...s.td, color: C.red, fontWeight: 700 }}>Pessimiste</td>
                {scenarios.pessimiste.map(s2 => <td key={s2.month} style={s.td}>{fmt(s2.ca)}</td>)}
              </tr>
              <tr>
                <td style={{ ...s.td, color: C.gold, fontWeight: 700 }}>Réaliste</td>
                {scenarios.realiste.map(s2 => <td key={s2.month} style={s.td}>{fmt(s2.ca)}</td>)}
              </tr>
              <tr>
                <td style={{ ...s.td, color: C.success, fontWeight: 700 }}>Optimiste</td>
                {scenarios.optimiste.map(s2 => <td key={s2.month} style={s.td}>{fmt(s2.ca)}</td>)}
              </tr>
            </tbody>
          </table>
        )}
        <div style={{ ...s.formSub, marginTop: 8 }}>
          Basés sur les taux de croissance mensuels le plus bas, moyen et le plus haut
          observés sur les 6 derniers mois — indicatif, pas un engagement.
        </div>
      </div>
    </div>
  );
}

function ResultatTab({ summary, fmt, monthLabel }) {
  const monthly = summary.monthly || [];
  const exp = summary.expenses;

  const tauxMargeVariable = summary.ca.month > 0
    ? (summary.ca.month - (exp.variableMonth || 0)) / summary.ca.month : null;
  const seuilRentabilite = tauxMargeVariable && tauxMargeVariable > 0
    ? (exp.fixedMonth || 0) / tauxMargeVariable : null;

  const exportCsv = () => downloadCSV('resultat.csv', monthly.map(m => ({
    mois: m.month, ca_fcfa: m.ca, charges_fcfa: m.charges, resultat_fcfa: m.resultat,
  })));

  const today = new Date().toLocaleDateString('fr-FR');

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div style={s.stat}>
          <div style={{ ...s.statVal, color: summary.resultatNet.month >= 0 ? C.success : C.red }}>
            {fmt(summary.resultatNet.month)}
          </div>
          <div style={s.statLabel}>Résultat net ce mois</div>
        </div>
        <div style={s.stat}>
          <div style={{ ...s.statVal, color: summary.resultatNet.year >= 0 ? C.success : C.red }}>
            {fmt(summary.resultatNet.year)}
          </div>
          <div style={s.statLabel}>Résultat net cette année</div>
        </div>
        <div style={s.stat}>
          <div style={s.statVal}>{fmt(exp.month)}</div>
          <div style={s.statLabel}>Charges ce mois ({fmt(exp.fixedMonth)} fixes)</div>
        </div>
        <div style={s.stat}>
          <div style={{ ...s.statVal, color: '#8A93AD' }}>
            {seuilRentabilite == null ? '—' : fmt(Math.round(seuilRentabilite))}
          </div>
          <div style={s.statLabel}>Seuil de rentabilité (CA min. mensuel)</div>
        </div>
      </div>

      <div style={s.info('blue')}>
        Seuil de rentabilité = charges fixes ÷ taux de marge sur coûts variables — le
        CA mensuel à atteindre pour couvrir toutes les charges. Basé sur le
        classement fixe/variable de chaque charge (onglet « Charges »), à ajuster
        selon la réalité de l'entreprise. Indicatif, pas un calcul comptable formel.
      </div>

      <div className="md-card" style={s.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={s.cardTitle}><Ic icon={BarChart3} color={C.green} /> CA, charges et résultat — 12 derniers mois</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...s.btn('#8A93AD'), padding: '7px 14px', fontSize: 11 }} onClick={exportCsv}>
              <Ic icon={FileText} color="#fff" size={12} /> Exporter CSV
            </button>
            <button style={{ ...s.btn(C.green), padding: '7px 14px', fontSize: 11 }} onClick={() => window.print()}>
              <Ic icon={FileText} color="#fff" size={12} /> Imprimer le compte de résultat
            </button>
          </div>
        </div>
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Mois</th>
            <th style={s.th}>CA</th>
            <th style={s.th}>Charges</th>
            <th style={s.th}>Résultat</th>
          </tr></thead>
          <tbody>
            {monthly.map(m => (
              <tr key={m.month}>
                <td style={s.td}>{monthLabel(m.month)}</td>
                <td style={s.td}>{fmt(m.ca)}</td>
                <td style={s.td}>{fmt(m.charges)}</td>
                <td style={{ ...s.td, color: m.resultat >= 0 ? C.success : C.red, fontWeight: 700 }}>
                  {fmt(m.resultat)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Version imprimable — invisible à l'écran, affichée uniquement à l'impression (voir @media print, public/index.html) */}
      <div className="print-area" style={{ padding: 24, fontFamily: 'sans-serif' }}>
        <h2>Mon Dépanneur CI — Compte de résultat simplifié</h2>
        <p>Édité le {today} — document interne, non certifié, à faire valider par un expert-comptable.</p>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }} border="1" cellPadding="8">
          <thead><tr><th>Mois</th><th>Chiffre d'affaires</th><th>Charges</th><th>Résultat net</th></tr></thead>
          <tbody>
            {monthly.map(m => (
              <tr key={m.month}>
                <td>{monthLabel(m.month)}</td><td>{fmt(m.ca)}</td><td>{fmt(m.charges)}</td><td>{fmt(m.resultat)}</td>
              </tr>
            ))}
            <tr style={{ fontWeight: 'bold' }}>
              <td>Total période</td>
              <td>{fmt(monthly.reduce((a, m) => a + m.ca, 0))}</td>
              <td>{fmt(monthly.reduce((a, m) => a + m.charges, 0))}</td>
              <td>{fmt(monthly.reduce((a, m) => a + m.resultat, 0))}</td>
            </tr>
          </tbody>
        </table>
        <p style={{ marginTop: 16 }}>Seuil de rentabilité mensuel indicatif : {seuilRentabilite == null ? 'non calculable' : fmt(Math.round(seuilRentabilite))}</p>
      </div>
    </div>
  );
}

function ChargesTab() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ category: 'services_exterieurs', label: '', amount: '', expense_date: '', note: '', is_fixed: true });

  const load = () => {
    setLoading(true);
    api.get('/api/admin/accounting/expenses')
      .then(r => { setExpenses(r.data.expenses || []); setError(''); })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Number(n) || 0) + ' FCFA';

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true); setError('');
    try {
      await api.post('/api/admin/accounting/expenses', {
        category: form.category, label: form.label.trim(),
        amount: Number(form.amount), expense_date: form.expense_date || undefined,
        note: form.note.trim() || undefined, is_fixed: form.is_fixed,
      });
      setForm({ category: 'services_exterieurs', label: '', amount: '', expense_date: '', note: '', is_fixed: true });
      load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setCreating(false); }
  };

  const updateField = async (id, patch) => {
    try { await api.put(`/api/admin/accounting/expenses/${id}`, patch); load(); }
    catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette charge ?')) return;
    try { await api.delete(`/api/admin/accounting/expenses/${id}`); load(); }
    catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const total = expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const totalFixed = expenses.reduce((sum, e) => sum + (e.is_fixed ? Number(e.amount) || 0 : 0), 0);
  const totalVariable = total - totalFixed;

  const exportCsv = () => downloadCSV('charges.csv', expenses.map(e => ({
    date: (e.expense_date || '').slice(0, 10), categorie: e.category, libelle: e.label,
    montant_fcfa: e.amount, fixe_ou_variable: e.is_fixed ? 'fixe' : 'variable', note: e.note || '',
  })));

  return (
    <div>
      <div className="md-card" style={s.card}>
        <div style={s.cardTitle}><Ic icon={Plus} color={C.green} /> Ajouter une charge</div>
        <form onSubmit={submit} style={s.formRow}>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Catégorie</label>
            <select style={s.input} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
              {EXPENSE_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Libellé</label>
            <input style={s.input} required placeholder="ex : Hébergement VPS Contabo"
                   value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} />
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Montant (FCFA)</label>
            <input style={s.input} required type="number" min="0" placeholder="ex : 25000"
                   value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Date</label>
            <input style={s.input} type="date"
                   value={form.expense_date} onChange={e => setForm({ ...form, expense_date: e.target.value })} />
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Nature</label>
            <select style={s.input} value={form.is_fixed ? 'fixe' : 'variable'}
                    onChange={e => setForm({ ...form, is_fixed: e.target.value === 'fixe' })}>
              <option value="fixe">Charge fixe</option>
              <option value="variable">Charge variable</option>
            </select>
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Note (optionnel)</label>
            <input style={s.input} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          </div>
        </form>
        <button type="submit" onClick={submit} style={{ ...s.btn(), padding: '10px 18px', marginTop: 8 }}
                disabled={creating || !form.label.trim() || !form.amount}>
          {creating ? '…' : 'Ajouter la charge'}
        </button>
        {error && <div style={{ ...s.info('red'), marginTop: 12 }}>{error}</div>}
      </div>

      <div className="md-card" style={s.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={s.cardTitle}><Ic icon={Banknote} color={C.gold} /> Charges enregistrées ({expenses.length})</div>
          <button style={{ ...s.btn('#8A93AD'), padding: '7px 14px', fontSize: 11 }} onClick={exportCsv}>
            <Ic icon={FileText} color="#fff" size={12} /> Exporter CSV
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <span style={s.badge(C.successL, C.success)}>Total {fmt(total)}</span>
          <span style={s.badge('#E1E5EF', '#4A5470')}>Fixes {fmt(totalFixed)}</span>
          <span style={s.badge('#E1E5EF', '#4A5470')}>Variables {fmt(totalVariable)}</span>
        </div>
        {loading ? <div>Chargement…</div> : (
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Date</th>
              <th style={s.th}>Catégorie</th>
              <th style={s.th}>Libellé</th>
              <th style={s.th}>Montant</th>
              <th style={s.th}>Nature</th>
              <th style={s.th}>Note</th>
              <th style={s.th}></th>
            </tr></thead>
            <tbody>
              {expenses.map(e => (
                <tr key={e.id}>
                  <td style={s.td}>
                    <input style={{ ...s.input, padding: '6px 8px' }} type="date" defaultValue={(e.expense_date || '').slice(0, 10)}
                           onBlur={ev => { if (ev.target.value !== (e.expense_date || '').slice(0, 10)) updateField(e.id, { expense_date: ev.target.value }); }} />
                  </td>
                  <td style={s.td}>
                    <select style={{ ...s.input, padding: '6px 8px' }} defaultValue={e.category}
                            onChange={ev => updateField(e.id, { category: ev.target.value })}>
                      {EXPENSE_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                  </td>
                  <td style={s.td}>
                    <input style={{ ...s.input, padding: '6px 8px' }} defaultValue={e.label}
                           onBlur={ev => { const v = ev.target.value.trim(); if (v && v !== e.label) updateField(e.id, { label: v }); }} />
                  </td>
                  <td style={s.td}>
                    <input style={{ ...s.input, padding: '6px 8px', width: 110 }} type="number" defaultValue={e.amount}
                           onBlur={ev => { const v = Number(ev.target.value); if (Number.isFinite(v) && v !== Number(e.amount)) updateField(e.id, { amount: v }); }} />
                  </td>
                  <td style={s.td}>
                    <select style={{ ...s.input, padding: '6px 8px' }} defaultValue={e.is_fixed ? 'fixe' : 'variable'}
                            onChange={ev => updateField(e.id, { is_fixed: ev.target.value === 'fixe' })}>
                      <option value="fixe">Fixe</option>
                      <option value="variable">Variable</option>
                    </select>
                  </td>
                  <td style={{ ...s.td, fontSize: 11, color: '#8A93AD' }}>{e.note || '—'}</td>
                  <td style={s.td}>
                    <button style={{ ...s.btn(C.red), padding: '6px 12px', fontSize: 11 }} onClick={() => remove(e.id)}>Suppr.</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ ...s.formSub, marginTop: 12 }}>
          Catégories inspirées des classes de charges SYSCOHADA, simplifiées pour un
          suivi courant — à faire valider par un expert-comptable pour un classement
          comptable formel.
        </div>
      </div>
    </div>
  );
}

function FiscalTab() {
  const [obligations, setObligations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/api/admin/accounting/tax-obligations')
      .then(r => { setObligations(r.data.obligations || []); setError(''); })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updateField = async (id, patch) => {
    try { await api.put(`/api/admin/accounting/tax-obligations/${id}`, patch); load(); }
    catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const statusTone = (status) => status === 'paye' ? [C.successL, C.success]
    : status === 'declare' ? [C.premiumL, C.premium] : [C.redL, C.red];
  const statusLabel = (status) => status === 'paye' ? 'Payée' : status === 'declare' ? 'Déclarée' : 'À faire';

  const in30days = new Date(); in30days.setDate(in30days.getDate() + 30);
  const dueSoon = obligations.filter(o =>
    o.status === 'a_faire' && o.next_due_date && new Date(o.next_due_date) <= in30days
  );

  const exportCsv = () => downloadCSV('declarations-fiscales.csv', obligations.map(o => ({
    obligation: o.label, taux_montant: o.rate_or_amount || '', periodicite: o.period,
    prochaine_echeance: (o.next_due_date || '').slice(0, 10), statut: statusLabel(o.status), note: o.note || '',
  })));

  return (
    <div className="md-card" style={s.card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={s.cardTitle}><Ic icon={FileText} color={'#6B7280'} /> Déclarations fiscales — Côte d'Ivoire</div>
        <button style={{ ...s.btn('#8A93AD'), padding: '7px 14px', fontSize: 11 }} onClick={exportCsv}>
          <Ic icon={FileText} color="#fff" size={12} /> Exporter CSV
        </button>
      </div>
      <div style={s.info('gold')}>
        Liste de départ éditable, pas un calcul automatique — taux, montants et
        échéances à vérifier et ajuster avec un expert-comptable agréé.
      </div>
      {dueSoon.length > 0 && (
        <div style={s.info('red')}>
          <Ic icon={AlertTriangle} color={C.red} size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
          <strong>{dueSoon.length} échéance{dueSoon.length > 1 ? 's' : ''} dans les 30 prochains jours</strong> :{' '}
          {dueSoon.map(o => `${o.label} (${(o.next_due_date || '').slice(0, 10)})`).join(', ')}
        </div>
      )}
      {error && <div style={s.info('red')}>{error}</div>}
      {loading ? <div>Chargement…</div> : (
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Obligation</th>
            <th style={s.th}>Taux / montant</th>
            <th style={s.th}>Périodicité</th>
            <th style={s.th}>Prochaine échéance</th>
            <th style={s.th}>Statut</th>
            <th style={s.th}>Note</th>
          </tr></thead>
          <tbody>
            {obligations.map(o => {
              const [bg, color] = statusTone(o.status);
              return (
                <tr key={o.id}>
                  <td style={s.td}>{o.label}</td>
                  <td style={s.td}>
                    <input style={{ ...s.input, padding: '6px 8px', width: 100 }} defaultValue={o.rate_or_amount || ''}
                           placeholder="ex : 18%"
                           onBlur={e => { if (e.target.value !== (o.rate_or_amount || '')) updateField(o.id, { rate_or_amount: e.target.value }); }} />
                  </td>
                  <td style={s.td}>
                    <select style={{ ...s.input, padding: '6px 8px' }} defaultValue={o.period}
                            onChange={e => updateField(o.id, { period: e.target.value })}>
                      <option value="mensuelle">Mensuelle</option>
                      <option value="trimestrielle">Trimestrielle</option>
                      <option value="annuelle">Annuelle</option>
                    </select>
                  </td>
                  <td style={s.td}>
                    <input style={{ ...s.input, padding: '6px 8px' }} type="date" defaultValue={(o.next_due_date || '').slice(0, 10)}
                           onChange={e => updateField(o.id, { next_due_date: e.target.value })} />
                  </td>
                  <td style={s.td}>
                    <select style={{ ...s.badge(bg, color), border: 'none', fontWeight: 700 }} defaultValue={o.status}
                            onChange={e => updateField(o.id, { status: e.target.value })}>
                      <option value="a_faire">À faire</option>
                      <option value="declare">Déclarée</option>
                      <option value="paye">Payée</option>
                    </select>
                  </td>
                  <td style={s.td}>
                    <input style={{ ...s.input, padding: '6px 8px' }} defaultValue={o.note || ''}
                           onBlur={e => { if (e.target.value !== (o.note || '')) updateField(o.id, { note: e.target.value }); }} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StaffPanel({ currentUser }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ contact: '', name: '', staff_role: 'comptable' });

  const isSuper = !currentUser?.staff_role || currentUser.staff_role === 'admin';

  const load = () => {
    setLoading(true);
    api.get('/api/admin/staff')
      .then(r => { setStaff(r.data.staff || []); setError(''); })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  };
  useAutoRefresh(() => {
    if (isSuper) load(); else setLoading(false);
  }, 60000, [isSuper]);

  if (!isSuper) {
    return (
      <div className="md-card" style={s.card}>
        <div style={s.cardTitle}><Ic icon={Users} color={C.gold} /> Équipe</div>
        <div style={s.info('red')}>
          Cette section est réservée au super-administrateur. Contacte-le pour
          créer ou gérer un compte staff.
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true); setError('');
    try {
      await api.post('/api/admin/staff', {
        contact: form.contact.trim(),
        name: form.name.trim(),
        staff_role: form.staff_role,
      });
      setForm({ contact: '', name: '', staff_role: 'comptable' });
      load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setCreating(false); }
  };

  const updateRole = async (id, staff_role) => {
    try { await api.patch(`/api/admin/staff/${id}`, { staff_role }); load(); }
    catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/api/admin/staff/${u.id}`, { is_active: !u.is_active }); load(); }
    catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce compte staff ? (il redevient un client désactivé)')) return;
    try { await api.delete(`/api/admin/staff/${id}`); load(); }
    catch (err) { alert(err.response?.data?.error || err.message); }
  };

  return (
    <div>
      {/* Création */}
      <div className="md-card" style={s.card}>
        <div style={s.cardTitle}><Ic icon={Plus} color={C.green} /> Inviter un nouveau membre</div>
        <div style={s.info('blue')}>
          Le nouveau membre se connecte avec son <strong>email ou téléphone</strong> et un
          code à 4 chiffres qu'il choisit lui-même à sa première connexion.
          Aucun mot de passe à transmettre.
        </div>
        <form onSubmit={submit} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ ...s.formGroup, flex: '1.5 1 200px' }}>
            <label style={{ fontSize: 11, color: '#8A93AD' }}>Email ou téléphone</label>
            <input style={s.input} required placeholder="ex: jean@equipe.ci"
                   value={form.contact} onChange={e => setForm({ ...form, contact: e.target.value })} />
          </div>
          <div style={{ ...s.formGroup, flex: '1.5 1 180px' }}>
            <label style={{ fontSize: 11, color: '#8A93AD' }}>Nom (optionnel)</label>
            <input style={s.input} placeholder="Jean Kouassi"
                   value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div style={{ ...s.formGroup, flex: '1 1 160px' }}>
            <label style={{ fontSize: 11, color: '#8A93AD' }}>Niveau d'accès</label>
            <select style={s.input} value={form.staff_role}
                    onChange={e => setForm({ ...form, staff_role: e.target.value })}>
              {STAFF_ROLES.map(r => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
          </div>
          <button type="submit" style={{ ...s.btn(), padding: '10px 18px' }} disabled={creating}>
            {creating ? '…' : 'Créer le compte'}
          </button>
        </form>
        {error && <div style={{ ...s.info('red'), marginTop: 12 }}>{error}</div>}

        {/* Légende rôles */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginTop: 18 }}>
          {STAFF_ROLES.map(r => (
            <div key={r.id} style={{ background: '#F5F7FC', borderRadius: 10, padding: 12, border: '1px solid #E1E5EF' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 13, color: r.color }}>
                <Ic icon={r.ico} color={r.color} size={15} />{r.label}
              </div>
              <div style={{ fontSize: 11, color: '#8A93AD', marginTop: 4, lineHeight: 1.4 }}>{r.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Liste */}
      <div className="md-card" style={s.card}>
        <div style={s.cardTitle}><Ic icon={Users} color={C.gold} /> Membres de l'équipe ({staff.length})</div>
        {loading ? <div>Chargement…</div> : (
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Membre</th>
              <th style={s.th}>Contact</th>
              <th style={s.th}>Niveau</th>
              <th style={s.th}>Statut</th>
              <th style={s.th}>Inscrit le</th>
              <th style={s.th}>Actions</th>
            </tr></thead>
            <tbody>
              {staff.map(u => {
                const meta = roleMeta(u.staff_role || 'admin');
                const isMe = u.id === currentUser?.id;
                return (
                  <tr key={u.id}>
                    <td style={s.td}>
                      <strong>{u.name || '—'}</strong>
                      {isMe && <span style={{ ...s.badge(C.greenL, C.green), marginLeft: 6 }}>moi</span>}
                    </td>
                    <td className="mono" style={s.tdMono}>{u.contact}</td>
                    <td style={s.td}>
                      <select style={{ ...s.input, padding: '6px 8px', width: 'auto', minWidth: 160 }}
                              disabled={isMe}
                              value={u.staff_role || 'admin'}
                              onChange={e => updateRole(u.id, e.target.value)}>
                        {STAFF_ROLES.map(r => (
                          <option key={r.id} value={r.id}>{r.label}</option>
                        ))}
                      </select>
                    </td>
                    <td style={s.td}>
                      {u.is_active
                        ? <span style={s.badge(C.greenL, C.green)}>✓ Actif</span>
                        : <span style={s.badge(C.redL, C.red)}>✗ Désactivé</span>}
                    </td>
                    <td style={s.td}>{new Date(u.created_at).toLocaleDateString('fr-FR')}</td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={{ ...s.btn(u.is_active ? C.gold : C.green), padding: '6px 10px', fontSize: 11 }}
                                disabled={isMe} onClick={() => toggleActive(u)}>
                          {u.is_active ? 'Désactiver' : 'Réactiver'}
                        </button>
                        <button style={{ ...s.btn(C.red), padding: '6px 10px', fontSize: 11 }}
                                disabled={isMe} onClick={() => remove(u.id)}>
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {staff.length === 0 && (
                <tr><td colSpan={6} style={s.td}>
                  <em style={{ color: '#8A93AD' }}>Aucun membre pour le moment.</em>
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── UTILISATEURS ────────────────────────────────────────
function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('');
  const [dossier, setDossier] = useState(null);

  const load = () => api.get(`/api/admin/users${role ? `?role=${role}` : ''}`).then(r => { setUsers(r.data.users || []); setLoading(false); });
  useAutoRefresh(load, 30000, [role]);

  const toggle = async (id) => {
    await api.post(`/api/admin/users/${id}/toggle`);
    load();
  };

  const reactivateLocked = async (id) => {
    const r = await api.post(`/api/admin/users/${id}/reactivate-locked`);
    alert(r.data?.message || 'Compte réactivé');
    load();
  };

  return (
    <div className="md-card" style={s.card}>
      <div style={s.cardTitle}><Ic icon={Users} color={C.green} /> Utilisateurs ({users.length})</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {['', 'client', 'artisan', 'admin'].map(r => (
          <button key={r || 'all'} style={{ ...s.btn(role === r ? C.green : '#8A93AD'), fontSize: 11 }} onClick={() => setRole(r)}>
            {r || 'Tous'}
          </button>
        ))}
      </div>
      {loading ? <div>Chargement...</div> :
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Contact</th><th style={s.th}>Nom</th><th style={s.th}>Rôle</th>
            <th style={s.th}>Commune</th><th style={s.th}>Statut</th><th style={s.th}>Inscrit</th><th style={s.th}>Action</th>
          </tr></thead>
          <tbody>
            {users.map(u => {
              const pinLocked = u.blocked_reason === 'pin_brute_force' &&
                u.blocked_until && new Date(u.blocked_until) > new Date();
              return (
              <tr key={u.id}>
                <td className="mono" style={s.tdMono}>{u.contact}</td>
                <td style={s.td}>
                  <button onClick={() => setDossier(u.id)}
                          style={{ background: 'none', border: 'none', padding: 0,
                                   cursor: 'pointer', font: 'inherit', fontWeight: 700,
                                   color: C.success, textDecoration: 'underline',
                                   textUnderlineOffset: 3 }}>
                    {u.name || u.contact}
                  </button>
                </td>
                <td style={s.td}><span style={s.badge(C.blueL, C.blue)}>{u.role}</span></td>
                <td style={s.td}>{u.commune || '—'}</td>
                <td style={s.td}>
                  {pinLocked
                    ? <span style={s.badge(C.redL, C.red)} title={`IP bloquée : ${u.pin_lock_ip || '—'}`}>🔒 PIN + IP bloqués</span>
                    : u.is_active ? <span style={s.badge(C.greenL, C.green)}>Actif</span> : <span style={s.badge(C.redL, C.red)}>Désactivé</span>}
                </td>
                <td style={s.td}>{new Date(u.created_at).toLocaleDateString('fr-FR')}</td>
                <td style={s.td}>
                  {pinLocked
                    ? <button style={s.btn(C.green)} onClick={() => reactivateLocked(u.id)}>Réactiver + envoyer code</button>
                    : <button style={s.btn(u.is_active ? C.red : C.green)} onClick={() => toggle(u.id)}>{u.is_active ? 'Bloquer' : 'Réactiver'}</button>}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>}

      {dossier && <UserDossierModal userId={dossier} onClose={() => setDossier(null)} />}
    </div>
  );
}

// ─── SIGNALEMENTS ────────────────────────────────────────
function ReportsPanel() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { type, report }

  const load = () => api.get('/api/admin/reports').then(r => { setReports(r.data.reports || []); setLoading(false); });
  useAutoRefresh(load, 20000);

  const resolve  = async (id) => { await api.post(`/api/admin/reports/${id}/resolve`); load(); };
  const unblock  = async (uid) => { await api.post(`/api/admin/users/${uid}/unblock`); load(); };

  return (
    <div className="md-card" style={s.card}>
      <div style={s.cardTitle}><Ic icon={AlertTriangle} color={C.red} /> Signalements ({reports.length})</div>
      {loading ? <div>Chargement...</div> :
        reports.length === 0 ? <div style={s.info('green')}>✅ Aucun signalement.</div> :
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Auteur</th>
            <th style={s.th}>Cible</th>
            <th style={s.th}>Motif</th>
            <th style={s.th}>Statut</th>
            <th style={s.th}>Date</th>
            <th style={s.th}>Actions</th>
          </tr></thead>
          <tbody>
            {reports.map(r => {
              const isArtisanSubject = r.subject_role === 'artisan' || (!r.subject_role && r.artisan_name);
              const targetName = isArtisanSubject
                ? (r.artisan_name ? `${r.artisan_name}${r.metier ? ' (' + r.metier + ')' : ''}` : r.target_name)
                : r.target_name;
              const blocked = r.target_blocked_until && new Date(r.target_blocked_until) > new Date();
              return (
                <tr key={r.id}>
                  <td style={s.td}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: r.reporter_role === 'artisan' ? C.gold : C.blue }}>
                      {r.reporter_role === 'artisan' ? '🔧 ARTISAN' : '🙋 CLIENT'}
                    </span><br />
                    {r.reporter_name}<br />
                    <span style={{ fontSize: 11, color: '#8A93AD' }}>{r.reporter_contact}</span>
                  </td>
                  <td style={s.td}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: isArtisanSubject ? C.gold : C.blue }}>
                      → {isArtisanSubject ? '🔧 ARTISAN' : '🙋 CLIENT'}
                    </span><br />
                    {targetName || '—'}<br />
                    <span style={{ fontSize: 11, color: '#8A93AD' }}>{r.target_contact || ''}</span>
                    {blocked && (
                      <div style={{ marginTop: 4, fontSize: 10, color: C.red, fontWeight: 700 }}>
                        ⛔ Bloqué jusqu'au {new Date(r.target_blocked_until).toLocaleString('fr-FR')}
                      </div>
                    )}
                  </td>
                  <td style={s.td}>{r.reason}</td>
                  <td style={s.td}><span style={s.badge(r.status === 'resolved' ? C.greenL : C.goldL, r.status === 'resolved' ? C.green : C.gold)}>{r.status}</span></td>
                  <td style={s.td}>{new Date(r.created_at).toLocaleDateString('fr-FR')}</td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {r.target_user_id && !blocked && (
                        <button style={s.btn(C.red)} onClick={() => setModal({ type: 'block', report: r })}>⛔ Bloquer</button>
                      )}
                      {r.target_user_id && blocked && (
                        <button style={s.btn(C.green)} onClick={() => unblock(r.target_user_id)}>✅ Débloquer</button>
                      )}
                      <button style={s.btn(C.blue)} onClick={() => setModal({ type: 'message', report: r })}>✉️ Message</button>
                      {r.status !== 'resolved' && (
                        <button style={s.btn()} onClick={() => resolve(r.id)}>Résoudre</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>}

      {modal && (
        <ReportActionModal
          report={modal.report}
          type={modal.type}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function ReportActionModal({ report, type, onClose, onDone }) {
  const [hours, setHours] = useState(24);
  const [reason, setReason] = useState('');
  const [body, setBody] = useState('');
  const [recipient, setRecipient] = useState(report.target_user_id || report.reporter_id);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (type === 'block') {
        await api.post(`/api/admin/users/${report.target_user_id}/block`, { hours: parseInt(hours), reason });
      } else if (type === 'message') {
        await api.post(`/api/admin/reports/${report.id}/message`, { userId: recipient, body });
      }
      onDone();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={onClose}>
      <div style={{ background: 'var(--surface-solid)', padding: 24, borderRadius: 18, width: 'min(480px, 92vw)', border: '1px solid var(--card-border)', boxShadow: 'var(--card-shadow)' }} onClick={e => e.stopPropagation()}>
        <div style={s.cardTitle}>
          {type === 'block'
            ? <><Ic icon={Ban} color={C.red} /> Bloquer temporairement</>
            : <><Ic icon={Mail} color={C.green} /> Envoyer un message</>}
        </div>

        {type === 'block' && (
          <>
            <div style={s.formGroup}>
              <div style={s.formLabel}>Durée du blocage (heures)</div>
              <input type="number" min={1} style={{ ...s.input, marginTop: 4 }}
                     value={hours} onChange={e => setHours(e.target.value)} />
              <div style={s.formSub}>Suggestions : 24h (1j) · 72h (3j) · 168h (7j)</div>
            </div>
            <div style={{ ...s.formGroup, marginTop: 12 }}>
              <div style={s.formLabel}>Motif (visible par l'utilisateur)</div>
              <textarea style={{ ...s.input, marginTop: 4, minHeight: 70, fontFamily: 'inherit' }}
                        value={reason} onChange={e => setReason(e.target.value)}
                        placeholder="Investigation en cours suite à signalement…" />
            </div>
          </>
        )}

        {type === 'message' && (
          <>
            <div style={s.formGroup}>
              <div style={s.formLabel}>Destinataire</div>
              <select style={{ ...s.input, marginTop: 4 }} value={recipient}
                      onChange={e => setRecipient(e.target.value)}>
                {report.target_user_id && (
                  <option value={report.target_user_id}>
                    Utilisateur signalé — {report.target_name || ''}
                  </option>
                )}
                <option value={report.reporter_id}>
                  Auteur du signalement — {report.reporter_name}
                </option>
              </select>
            </div>
            <div style={{ ...s.formGroup, marginTop: 12 }}>
              <div style={s.formLabel}>Message</div>
              <textarea style={{ ...s.input, marginTop: 4, minHeight: 100, fontFamily: 'inherit' }}
                        value={body} onChange={e => setBody(e.target.value)}
                        placeholder="Bonjour, suite à votre signalement…" />
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button style={s.btn(C.gray || '#8A93AD')} onClick={onClose} disabled={busy}>Annuler</button>
          <button style={s.btn()} onClick={submit}
                  disabled={busy || (type === 'message' && !body.trim()) || (type === 'block' && !hours)}>
            {busy ? '⏳…' : (type === 'block' ? 'Bloquer' : 'Envoyer')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── DOCUMENTS LÉGAUX (CGU) ──────────────────────────────
function LegalPanel({ currentUser }) {
  const [docs, setDocs] = useState([]);
  const [type, setType] = useState('cgu_client');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [requireReaccept, setRequireReaccept] = useState(true);
  const [summary, setSummary] = useState('');

  const isSuper = !currentUser?.staff_role || currentUser.staff_role === 'admin';

  const load = () => api.get('/api/admin/legal')
    .then(r => setDocs(r.data.documents || []))
    .catch(() => {});

  useEffect(() => { load(); }, []);

  // Charge la version publiée du type sélectionné dans l'éditeur
  useEffect(() => {
    const cur = docs.find(d => d.doc_type === type && d.is_published);
    setTitle(cur?.title || '');
    setContent(cur?.content || '');
  }, [type, docs]);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 5000); };

  const publish = async () => {
    const who = type === 'cgu_artisan' ? 'les dépanneurs' : 'les clients';
    if (!window.confirm(
      requireReaccept
        ? `Publier et exiger une nouvelle acceptation ?\n\n` +
          `Tous ${who} déjà inscrits devront relire et réaccepter au prochain ` +
          `lancement de l'app avant de pouvoir l'utiliser.`
        : `Publier sans redemander d'acceptation ?\n\n` +
          `Le nouveau texte s'appliquera aux nouvelles inscriptions. ` +
          `Les personnes déjà inscrites ne seront pas re-sollicitées — ` +
          `à réserver aux corrections mineures.`
    )) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/api/admin/legal/${type}`, {
        title, content,
        requireReacceptance: requireReaccept,
        changeSummary: summary,
      });
      flash(
        `✅ Version ${data.document.version} publiée` +
        (data.impacted ? ` — ${data.impacted} utilisateur(s) devront réaccepter` : '')
      );
      setSummary('');
      load();
    } catch (e) {
      flash('❌ ' + (e.response?.data?.error || 'Échec de la publication'));
    } finally { setSaving(false); }
  };

  const importFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      setContent(String(reader.result || ''));
      flash('📄 Fichier chargé — relis puis publie.');
    };
    reader.onerror = () => flash('❌ Lecture du fichier impossible');
    reader.readAsText(f, 'utf-8');
    e.target.value = '';
  };

  const current = docs.find(d => d.doc_type === type && d.is_published);
  const history = docs.filter(d => d.doc_type === type);
  const fmt = (x) => x ? new Date(x).toLocaleString('fr-FR') : '—';

  const TYPES = [
    ['cgu_client', '👤 CGU Client'],
    ['cgu_artisan', '🔧 CGU Dépanneur'],
    ['privacy', '🔒 Confidentialité'],
  ];

  return (
    <div>
      {msg && <div style={s.info(msg.startsWith('✅') || msg.startsWith('📄') ? 'green' : 'red')}>{msg}</div>}

      {!isSuper && (
        <div style={s.info('gold')}>
          Seul le super-administrateur peut publier une nouvelle version.
          Tu peux consulter les textes en cours.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {TYPES.map(([id, label]) => (
          <button key={id} onClick={() => setType(id)}
            style={{ ...s.btn(type === id ? C.green : '#4A5470'),
                     opacity: type === id ? 1 : .65, fontSize: 12 }}>
            {label}
          </button>
        ))}
      </div>

      <div className="md-card" style={s.card}>
        <div style={s.cardTitle}>
          <Ic icon={FileText} color={'#6B7280'} /> {TYPES.find(t => t[0] === type)?.[1]}
          {current && (
            <span style={{ ...s.badge(C.greenL, C.green), marginLeft: 8 }}>
              v{current.version} · publiée le {fmt(current.published_at)}
            </span>
          )}
        </div>

        <div style={s.info('blue')}>
          Ce texte s'affiche dans l'application au moment de l'inscription.
          L'utilisateur doit le faire défiler <strong>jusqu'en bas</strong> avant de
          pouvoir accepter, et la version acceptée est horodatée.
        </div>

        <div style={s.formGroup}>
          <div style={s.formLabel}>Titre affiché</div>
          <input style={{ ...s.input, marginTop: 4 }} value={title}
                 disabled={!isSuper}
                 placeholder="Conditions générales — Client"
                 onChange={e => setTitle(e.target.value)} />
        </div>

        <div style={{ ...s.formGroup, marginTop: 14 }}>
          <div style={s.formLabel}>Texte du document</div>
          <div style={s.formSub}>
            Texte brut. Les sauts de ligne sont conservés.
            {content.length > 0 && ` · ${content.length} caractères`}
          </div>
          <textarea
            style={{ ...s.input, marginTop: 6, minHeight: 380, fontFamily: 'inherit',
                     lineHeight: 1.65, resize: 'vertical' }}
            value={content}
            disabled={!isSuper}
            placeholder="Colle ici le texte complet des conditions générales…"
            onChange={e => setContent(e.target.value)} />
        </div>

        {isSuper && (
          <>
            <div style={{ marginTop: 16, padding: 14, borderRadius: 14,
                          background: 'var(--th-bg)', border: '1px solid var(--card-border)' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10,
                              cursor: 'pointer', fontSize: 13, color: 'var(--text-strong)',
                              fontWeight: 600 }}>
                <input type="checkbox" checked={requireReaccept}
                       onChange={e => setRequireReaccept(e.target.checked)}
                       style={{ width: 18, height: 18, marginTop: 1,
                                accentColor: C.green, cursor: 'pointer' }} />
                <span>
                  Exiger une nouvelle acceptation de tous les utilisateurs déjà inscrits
                  <div style={{ ...s.formSub, fontWeight: 400 }}>
                    Coché : chacun devra relire et réaccepter au prochain lancement de l'app
                    avant de pouvoir s'en servir. Décoché : seules les nouvelles inscriptions
                    verront ce texte — à réserver aux corrections de forme.
                  </div>
                </span>
              </label>

              {requireReaccept && (
                <div style={{ marginTop: 12 }}>
                  <div style={s.formLabel}>Résumé des changements (facultatif)</div>
                  <div style={s.formSub}>
                    Affiché en tête du document dans l'app. Expliquer ce qui change
                    évite que les gens acceptent sans lire.
                  </div>
                  <input style={{ ...s.input, marginTop: 6 }} value={summary}
                         placeholder="Ex : ajout des règles d'annulation et du délai de rétractation"
                         onChange={e => setSummary(e.target.value)} />
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <button style={s.btn()} onClick={publish}
                    disabled={saving || content.trim().length < 100}>
              {saving ? '⏳ Publication…' : '📤 Publier cette version'}
            </button>

            <label style={{ ...s.btn('#4A5470'), cursor: 'pointer' }}>
              📄 Importer un fichier .txt / .md
              <input type="file" accept=".txt,.md,text/plain,text/markdown"
                     style={{ display: 'none' }} onChange={importFile} />
            </label>

            <button style={s.btn('#4A5470')} onClick={() => setShowHistory(v => !v)}>
              🕘 Historique ({history.length})
            </button>

            {content.trim().length < 100 && content.length > 0 && (
              <span style={{ fontSize: 11.5, color: C.red }}>
                Trop court — 100 caractères minimum.
              </span>
            )}
            </div>
          </>
        )}
      </div>

      {showHistory && (
        <div className="md-card" style={s.card}>
          <div style={s.cardTitle}><Ic icon={Clock} color={'#6B7280'} /> Versions précédentes</div>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Version</th><th style={s.th}>Titre</th>
              <th style={s.th}>Publiée le</th><th style={s.th}>Par</th>
              <th style={s.th}>Statut</th><th style={s.th}>Taille</th>
            </tr></thead>
            <tbody>
              {history.map(d => (
                <tr key={d.id}>
                  <td style={s.td}><strong>v{d.version}</strong></td>
                  <td style={s.td}>{d.title}</td>
                  <td style={s.td}>{fmt(d.published_at || d.created_at)}</td>
                  <td style={s.td}>{d.updated_by_name || '—'}</td>
                  <td style={s.td}>
                    {d.is_published
                      ? <span style={s.badge(C.greenL, C.green)}>En vigueur</span>
                      : <span style={s.badge('#F5F7FC', '#8A93AD')}>Archivée</span>}
                  </td>
                  <td style={s.td}>{(d.content || '').length} car.</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── SUPPORT CLIENT ──────────────────────────────────────
function SupportPanel() {
  const [threads, setThreads] = useState([]);
  const [statusFilter, setStatusFilter] = useState('attente_admin');
  const [open, setOpen] = useState(null);     // id du thread ouvert
  const [thread, setThread] = useState(null); // détail + messages
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [remember, setRemember] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 4000); };

  const search = () => {
    api.get('/api/admin/support/threads', { params: statusFilter ? { status: statusFilter } : {} })
      .then(r => setThreads(r.data.threads || []))
      .catch(() => {});
  };
  useAutoRefresh(search, 60000, [statusFilter]);

  const loadThread = async (id) => {
    try {
      const { data } = await api.get(`/api/admin/support/thread/${id}`);
      setThread(data.thread);
    } catch (_) { /* on garde l'affichage courant */ }
  };

  const openThread = async (t) => {
    setOpen(t.id); setLoading(true); setThread(null);
    await loadThread(t.id);
    setLoading(false);
  };

  useAutoRefresh(() => open && loadThread(open), 15000, [open]);

  const STATUS_LABEL = {
    bot: ['En cours de tri', C.gray], attente_admin: ['En attente', C.gold],
    en_cours: ['En cours', C.blue], resolu: ['Résolu', C.success],
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/api/admin/support/thread/${open}/reply`, { content: reply.trim(), remember });
      setReply('');
      setRemember(false);
      await loadThread(open);
      search();
    } catch (e) { flash('❌ ' + (e.response?.data?.error || 'Échec')); }
    finally { setSending(false); }
  };

  const resolveThread = async () => {
    if (!window.confirm('Marquer cette conversation comme résolue ?')) return;
    try {
      await api.post(`/api/admin/support/thread/${open}/resolve`);
      flash('✅ Conversation marquée résolue');
      setOpen(null); setThread(null);
      search();
    } catch (e) { flash('❌ ' + (e.response?.data?.error || 'Échec')); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleString('fr-FR') : '—';

  // ── Détail d'une conversation ──
  if (open) {
    return (
      <div>
        {msg && <div style={s.info(msg.startsWith('✅') ? 'green' : 'red')}>{msg}</div>}
        <button style={{ ...s.btn(), marginBottom: 14 }} onClick={() => { setOpen(null); setThread(null); }}>
          ← Retour
        </button>
        {loading && !thread ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-mute)' }}>Chargement…</div>
        ) : thread && (
          <div className="md-card" style={s.card}>
            <div style={s.cardTitle}>
              {thread.name || thread.contact} · {thread.category || 'Autre'}
              {thread.status && (
                <span style={{
                  marginLeft: 10, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999,
                  background: STATUS_LABEL[thread.status][1] + '22', color: STATUS_LABEL[thread.status][1],
                }}>
                  {STATUS_LABEL[thread.status][0]}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflowY: 'auto', padding: '10px 0' }}>
              {thread.messages.map(m => (
                <div key={m.id} style={{
                  alignSelf: m.sender === 'user' ? 'flex-start' : 'flex-end',
                  maxWidth: '75%',
                  background: m.sender === 'user' ? 'var(--surface-solid)'
                            : m.sender === 'admin' ? C.greenL : C.line,
                  border: '1px solid var(--card-border)',
                  borderRadius: 12, padding: '8px 12px',
                }}>
                  <div style={{ fontSize: 10, color: 'var(--text-mute)', marginBottom: 2 }}>
                    {m.sender === 'user' ? (thread.name || 'Client') : m.sender === 'admin' ? 'Vous' : 'Bot'} · {fmt(m.created_at)}
                  </div>
                  <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{m.content}</div>
                </div>
              ))}
            </div>
            {thread.status !== 'resolu' && (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <textarea style={{ ...s.input, flex: 1, minHeight: 60, resize: 'vertical' }}
                            value={reply} onChange={e => setReply(e.target.value)}
                            placeholder="Votre réponse…" />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button style={s.btn(C.green)} disabled={sending || !reply.trim()} onClick={sendReply}>Envoyer</button>
                    <button style={s.btn(C.red)} onClick={resolveThread}>Marquer résolu</button>
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: 'var(--text-mute)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />
                  💡 Retenir comme réponse type (le bot IA pourra la réutiliser pour des questions similaires)
                </label>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Liste ──
  return (
    <div>
      {msg && <div style={s.info(msg.startsWith('✅') ? 'green' : 'red')}>{msg}</div>}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {[['attente_admin', 'En attente'], ['en_cours', 'En cours'], ['resolu', 'Résolus'], ['', 'Tous']].map(([id, label]) => (
          <button key={id || 'tous'} style={s.btn(statusFilter === id ? C.green : '#4A5470')}
                  onClick={() => setStatusFilter(id)}>
            {label}
          </button>
        ))}
      </div>
      <div className="md-card" style={s.card}>
        <div style={s.cardTitle}><Ic icon={MessageCircle} color={C.green} /> Demandes de support</div>
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Client / dépanneur</th>
            <th style={s.th}>Catégorie</th>
            <th style={s.th}>Dernier message</th>
            <th style={s.th}>Statut</th>
            <th style={s.th}>Mis à jour</th>
            <th style={s.th}></th>
          </tr></thead>
          <tbody>
            {threads.map(t => (
              <tr key={t.id}>
                <td style={s.td}>
                  <div style={{ fontWeight: 600 }}>{t.name || t.contact}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-mute)' }}>{t.role}</div>
                </td>
                <td style={s.td}>{t.category || 'Autre'}</td>
                <td style={{ ...s.td, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.last_message}
                </td>
                <td style={s.td}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999,
                    background: STATUS_LABEL[t.status][1] + '22', color: STATUS_LABEL[t.status][1],
                  }}>
                    {STATUS_LABEL[t.status][0]}
                  </span>
                </td>
                <td style={s.td}>{fmt(t.updated_at)}</td>
                <td style={s.td}>
                  <button style={s.btn()} onClick={() => openThread(t)}>Ouvrir</button>
                </td>
              </tr>
            ))}
            {!threads.length && (
              <tr><td style={s.td} colSpan={6}>Aucune conversation.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── LITIGES / MODÉRATION ────────────────────────────────
function ModerationPanel() {
  const [threads, setThreads] = useState([]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);   // { aId, bId }
  const [file, setFile] = useState(null);   // dossier complet
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('messages');
  const [zoom, setZoom] = useState(null);

  const search = () => {
    api.get('/api/admin/moderation/threads', { params: { q } })
      .then(r => setThreads(r.data.threads || []))
      .catch(() => {});
  };

  useAutoRefresh(search, 60000, []);

  const openThread = async (t) => {
    setOpen({ aId: t.a_id, bId: t.b_id });
    setLoading(true); setFile(null); setTab('messages');
    try {
      const { data } = await api.get(`/api/admin/moderation/thread/${t.a_id}/${t.b_id}`);
      setFile(data);
    } catch (e) {
      setFile({ error: e.response?.data?.error || 'Chargement impossible' });
    } finally { setLoading(false); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleString('fr-FR') : '—';
  const isImg = (a) => a.type === 'image' || /\.(jpe?g|png|webp|heic)$/i.test(a.url || '');

  /** Rechargement du dossier après une suppression. */
  const reloadFile = async () => {
    if (!open) return;
    try {
      const { data } = await api.get(`/api/admin/moderation/thread/${open.aId}/${open.bId}`);
      setFile(data);
    } catch (_) { /* on garde l'affichage courant */ }
  };

  const deleteAttachment = async (messageId) => {
    const reason = window.prompt(
      'Supprimer cette pièce jointe ?\n\n' +
      'Le message reste dans la conversation avec la mention « retirée par la modération ».\n' +
      'Le fichier est effacé du serveur — cette action est définitive.\n\n' +
      'Motif (conservé au journal d\'audit) :'
    );
    if (reason === null) return;
    try {
      const { data } = await api.delete(
        `/api/admin/moderation/attachment/${messageId}`,
        { data: { reason } }
      );
      await reloadFile();
      window.alert('✅ ' + data.message);
    } catch (e) {
      window.alert('❌ ' + (e.response?.data?.error || 'Suppression impossible'));
    }
  };

  const deleteInterventionPhoto = async (interventionId, url) => {
    const reason = window.prompt(
      'Supprimer cette photo de fin d\'intervention ?\n\n' +
      '⚠️ Elle sert de preuve que le travail a été réalisé. La supprimer peut ' +
      'affaiblir la position du dépanneur en cas de contestation.\n\n' +
      'Motif (conservé au journal d\'audit) :'
    );
    if (reason === null) return;
    try {
      await api.delete('/api/admin/moderation/intervention-photo', {
        data: { interventionId, url, reason },
      });
      await reloadFile();
      window.alert('✅ Photo supprimée');
    } catch (e) {
      window.alert('❌ ' + (e.response?.data?.error || 'Suppression impossible'));
    }
  };

  // ── Liste ──
  if (!open) {
    return (
      <div>
        <div style={s.info('gold')}>
          ⚖️ Ces conversations sont privées. Chaque consultation est inscrite au
          journal d'audit. À n'utiliser que pour instruire une réclamation.
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input style={{ ...s.input, flex: 1 }} value={q}
                 placeholder="Rechercher un nom ou un contact…"
                 onChange={e => setQ(e.target.value)}
                 onKeyDown={e => e.key === 'Enter' && search()} />
          <button style={s.btn()} onClick={search}>🔍 Rechercher</button>
        </div>

        <div className="md-card" style={s.card}>
          <div style={s.cardTitle}><Ic icon={MessageCircle} color={C.green} /> Échanges client ↔ dépanneur</div>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Participants</th>
              <th style={s.th}>Messages</th>
              <th style={s.th}>Pièces jointes</th>
              <th style={s.th}>Interventions</th>
              <th style={s.th}>Signalements</th>
              <th style={s.th}>Dernier échange</th>
              <th style={s.th}></th>
            </tr></thead>
            <tbody>
              {threads.map(t => (
                <tr key={`${t.a_id}-${t.b_id}`}>
                  <td style={s.td}>
                    <div style={{ fontWeight: 600 }}>{t.a_name || t.a_contact}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-mute)' }}>{t.a_role}</div>
                    <div style={{ margin: '3px 0', color: 'var(--text-mute)' }}>↕</div>
                    <div style={{ fontWeight: 600 }}>{t.b_name || t.b_contact}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-mute)' }}>{t.b_role}</div>
                  </td>
                  <td style={s.td}>{t.message_count}</td>
                  <td style={s.td}>
                    {t.attachment_count > 0
                      ? <strong style={{ color: C.gold }}>📎 {t.attachment_count}</strong>
                      : '—'}
                  </td>
                  <td style={s.td}>{t.intervention_count || '—'}</td>
                  <td style={s.td}>
                    {t.report_count > 0
                      ? <strong style={{ color: C.red }}>🚨 {t.report_count}</strong>
                      : '—'}
                  </td>
                  <td style={s.td}>{fmt(t.last_at)}</td>
                  <td style={s.td}>
                    <button style={{ ...s.btn(), padding: '6px 12px', fontSize: 11 }}
                            onClick={() => openThread(t)}>
                      Ouvrir le dossier
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {threads.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-mute)', fontSize: 13 }}>
              Aucun échange trouvé.
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Dossier ──
  const [uA, uB] = file?.users || [];
  const nameOf = (id) => (file?.users || []).find(u => u.id === id)?.name || 'Inconnu';

  return (
    <div>
      <button style={{ ...s.btn('#4A5470'), marginBottom: 14 }}
              onClick={() => { setOpen(null); setFile(null); }}>
        ← Retour à la liste
      </button>

      {loading && <div style={s.info('blue')}>⏳ Chargement du dossier…</div>}
      {file?.error && <div style={s.info('red')}>❌ {file.error}</div>}

      {file && !file.error && (
        <>
          <div className="md-card" style={s.card}>
            <div style={s.cardTitle}><Ic icon={Users} color={C.gold} /> Participants</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              {[uA, uB].filter(Boolean).map(u => (
                <div key={u.id} style={{
                  background: 'var(--th-bg)', borderRadius: 14, padding: 13,
                  border: '1px solid var(--card-border)',
                }}>
                  <div style={{ fontWeight: 700, color: 'var(--text-strong)' }}>{u.name || '—'}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>{u.contact}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-mute)', marginTop: 4 }}>
                    {u.role} · {u.commune || 'commune non renseignée'}
                  </div>
                  {u.blocked_until && new Date(u.blocked_until) > new Date() && (
                    <div style={{ fontSize: 11, color: C.red, marginTop: 4, fontWeight: 700 }}>
                      🚫 Bloqué jusqu'au {fmt(u.blocked_until)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, margin: '14px 0', flexWrap: 'wrap' }}>
            {[
              ['messages', `💬 Messages (${file.messages.length})`],
              ['evidence', `📎 Preuves (${file.evidence.length})`],
              ['interventions', `🛠️ Interventions (${file.interventions.length})`],
              ['reports', `🚨 Signalements (${file.reports.length})`],
            ].map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)}
                style={{ ...s.btn(tab === id ? C.green : '#4A5470'),
                         opacity: tab === id ? 1 : 0.65, fontSize: 12 }}>
                {label}
              </button>
            ))}
          </div>

          {/* Messages */}
          {tab === 'messages' && (
            <div className="md-card" style={s.card}>
              <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                {file.messages.map(m => {
                  const fromA = m.sender_id === uA?.id;
                  return (
                    <div key={m.id} style={{
                      display: 'flex',
                      justifyContent: fromA ? 'flex-start' : 'flex-end',
                      marginBottom: 10,
                    }}>
                      <div style={{
                        maxWidth: '72%',
                        background: fromA ? '#F5F7FC' : C.goldL,
                        border: '1px solid var(--card-border)',
                        borderRadius: 14, padding: 10,
                      }}>
                        <div style={{ fontSize: 10, color: 'var(--text-mute)', marginBottom: 4 }}>
                          {m.sender_name || nameOf(m.sender_id)} · {fmt(m.created_at)}
                        </div>
                        {m.attachment_url && (
                          <div style={{ position: 'relative', marginBottom: 6 }}>
                            {isImg(m)
                              ? <img src={m.attachment_url} alt="pièce jointe"
                                     onClick={() => setZoom(m.attachment_url)}
                                     style={{ maxWidth: '100%', borderRadius: 10,
                                              cursor: 'zoom-in', display: 'block' }} />
                              : <a href={m.attachment_url} target="_blank" rel="noreferrer"
                                   style={{ display: 'block', fontSize: 12 }}>
                                  📄 {m.attachment_name || 'Document'}
                                </a>}
                            <button
                              title="Supprimer cette pièce jointe"
                              onClick={() => deleteAttachment(m.id)}
                              style={{
                                position: 'absolute', top: 6, right: 6,
                                width: 26, height: 26, borderRadius: 999,
                                border: 'none', cursor: 'pointer',
                                background: 'rgba(220,38,38,.92)', color: '#fff',
                                fontSize: 13, lineHeight: 1,
                              }}>🗑</button>
                          </div>
                        )}
                        {m.content && (
                          <div style={{ fontSize: 13, color: 'var(--text-strong)', whiteSpace: 'pre-wrap' }}>
                            {m.content}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {file.messages.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-mute)', fontSize: 13 }}>
                    Aucun message.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Preuves */}
          {tab === 'evidence' && (
            <div className="md-card" style={s.card}>
              <div style={s.cardTitle}><Ic icon={Paperclip} color={'#6B7280'} /> Toutes les preuves visuelles</div>
              <div style={s.info('blue')}>
                Pièces jointes de la conversation et photos de fin d'intervention,
                du plus récent au plus ancien.
              </div>
              <div style={{ display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))',
                            gap: 12 }}>
                {file.evidence.map((e, i) => (
                  <div key={i} style={{ border: '1px solid var(--card-border)', borderRadius: 12,
                                        overflow: 'hidden', background: 'var(--surface-solid)',
                                        position: 'relative' }}>
                    {isImg(e)
                      ? <img src={e.url} alt={e.name || 'preuve'}
                             onClick={() => setZoom(e.url)}
                             style={{ width: '100%', height: 130, objectFit: 'cover',
                                      cursor: 'zoom-in', display: 'block' }} />
                      : <a href={e.url} target="_blank" rel="noreferrer"
                           style={{ display: 'block', padding: 30, textAlign: 'center',
                                    fontSize: 28, textDecoration: 'none' }}>📄</a>}
                    {e.source === 'intervention' && e.interventionId && (
                      <button
                        title="Supprimer cette photo"
                        onClick={() => deleteInterventionPhoto(e.interventionId, e.url)}
                        style={{
                          position: 'absolute', top: 6, right: 6,
                          width: 26, height: 26, borderRadius: 999,
                          border: 'none', cursor: 'pointer',
                          background: 'rgba(220,38,38,.92)', color: '#fff',
                          fontSize: 13, lineHeight: 1,
                        }}>🗑</button>
                    )}
                    <div style={{ padding: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-strong)' }}>
                        {e.source === 'intervention' ? '🛠️ Fin d\'intervention' : '💬 Conversation'}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-mute)' }}>
                        {e.by || '—'} · {fmt(e.at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {file.evidence.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-mute)', fontSize: 13 }}>
                  Aucune pièce jointe ni photo d'intervention.
                </div>
              )}
            </div>
          )}

          {/* Interventions */}
          {tab === 'interventions' && (
            <div className="md-card" style={s.card}>
              <div style={s.cardTitle}><Ic icon={Hammer} color={C.gold} /> Interventions entre ces deux personnes</div>
              <table style={s.table}>
                <thead><tr>
                  <th style={s.th}>Date</th><th style={s.th}>Métier</th>
                  <th style={s.th}>Statut</th><th style={s.th}>Montant</th>
                  <th style={s.th}>Note</th><th style={s.th}>Détail</th>
                </tr></thead>
                <tbody>
                  {file.interventions.map(i => (
                    <tr key={i.id}>
                      <td style={s.td}>{fmt(i.created_at)}</td>
                      <td style={s.td}>{i.metier}</td>
                      <td style={s.td}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 999,
                          background: i.status === 'paid' ? C.successL
                                    : i.status === 'cancelled' || i.status === 'refused' ? C.redL
                                    : C.goldL,
                          color: i.status === 'paid' ? C.success
                               : i.status === 'cancelled' || i.status === 'refused' ? C.red
                               : C.goldD,
                        }}>{i.status}</span>
                      </td>
                      <td style={s.td}>
                        {i.final_price ? `${i.final_price} F`
                          : i.proposed_price ? `${i.proposed_price} F (devis)` : '—'}
                      </td>
                      <td style={s.td}>{i.rating ? `${i.rating}/5` : '—'}</td>
                      <td style={s.td}>
                        <div style={{ fontSize: 11, color: 'var(--text-soft)', maxWidth: 280 }}>
                          {i.description}
                        </div>
                        {i.cancel_reason && (
                          <div style={{ fontSize: 11, color: C.red, marginTop: 3 }}>
                            Motif : {i.cancel_reason}
                          </div>
                        )}
                        {i.review_comment && (
                          <div style={{ fontSize: 11, color: 'var(--text-mute)', marginTop: 3, fontStyle: 'italic' }}>
                            « {i.review_comment} »
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {file.interventions.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-mute)', fontSize: 13 }}>
                  Aucune intervention entre ces deux personnes.
                </div>
              )}
            </div>
          )}

          {/* Signalements */}
          {tab === 'reports' && (
            <div className="md-card" style={s.card}>
              <div style={s.cardTitle}><Ic icon={AlertTriangle} color={C.red} /> Signalements déposés</div>
              {file.reports.length === 0
                ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-mute)', fontSize: 13 }}>
                    Aucun signalement.
                  </div>
                : file.reports.map(r => (
                    <div key={r.id} style={{
                      border: '1px solid var(--card-border)', borderRadius: 12,
                      padding: 12, marginBottom: 10,
                    }}>
                      <div style={{ fontSize: 11, color: 'var(--text-mute)' }}>
                        {r.reporter_name || '—'} · {fmt(r.created_at)} · {r.status}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--text-strong)', marginTop: 4 }}>{r.reason}</div>
                    </div>
                  ))}
            </div>
          )}
        </>
      )}

      {/* Agrandissement d'image */}
      {zoom && (
        <div onClick={() => setZoom(null)}
             style={{
               position: 'fixed', inset: 0, background: 'rgba(0,0,0,.88)',
               display: 'flex', alignItems: 'center', justifyContent: 'center',
               zIndex: 1000, cursor: 'zoom-out', padding: 30,
             }}>
          <img src={zoom} alt="agrandissement"
               style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}

// ─── SÉCURITÉ ────────────────────────────────────────────
function MfaSetup() {
  const [enabled, setEnabled] = useState(null);
  const [setupData, setSetupData] = useState(null); // { secret, qrCodeDataUrl }
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [disabling, setDisabling] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => {
    api.get('/api/auth/mfa/status').then(r => setEnabled(r.data.enabled)).catch(() => setEnabled(false));
  };
  useEffect(load, []);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 5000); };

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/api/auth/mfa/setup');
      setSetupData(data);
    } catch (e) { flash('❌ ' + (e.response?.data?.error || 'Échec')); }
    finally { setBusy(false); }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/api/auth/mfa/confirm', { code });
      setBackupCodes(data.backupCodes);
      setSetupData(null);
      setCode('');
    } catch (e) { flash('❌ ' + (e.response?.data?.error || 'Code invalide')); }
    finally { setBusy(false); }
  };

  const finishAfterBackup = () => {
    setBackupCodes(null);
    load();
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/auth/mfa/disable', { code: disableCode });
      setDisabling(false); setDisableCode('');
      flash('✅ Double authentification désactivée');
      load();
    } catch (e) { flash('❌ ' + (e.response?.data?.error || 'Code invalide')); }
    finally { setBusy(false); }
  };

  if (enabled === null) return <div style={{ color: 'var(--text-mute)' }}>Chargement…</div>;

  return (
    <div className="md-card" style={s.card}>
      <div style={s.cardTitle}><Ic icon={Key} color={C.red} /> Double authentification (TOTP)</div>
      <div style={s.info('blue')}>
        💡 Ajoute un code à usage unique (app d'authentification — Google Authenticator, Authy, 1Password…)
        en plus du code reçu par email à chaque connexion. Réglage personnel : chaque admin l'active pour son propre compte.
      </div>
      {msg && <div style={s.info(msg.startsWith('✅') ? 'green' : 'red')}>{msg}</div>}

      {backupCodes ? (
        <div>
          <div style={s.info('gold')}>
            ⚠️ <strong>Note ces codes de secours maintenant</strong> — ils ne seront plus jamais affichés.
            Chacun ne fonctionne qu'une fois, en cas de perte de ton téléphone.
          </div>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, margin: '12px 0',
            fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 13,
          }}>
            {backupCodes.map(c => (
              <div key={c} style={{ background: 'var(--th-bg)', padding: '8px 12px', borderRadius: 8 }}>{c}</div>
            ))}
          </div>
          <button style={{ ...s.btn(C.green) }} onClick={finishAfterBackup}>
            ✅ J'ai sauvegardé mes codes
          </button>
        </div>
      ) : enabled ? (
        <div>
          <div style={s.info('green')}>✅ Double authentification activée sur ton compte.</div>
          {!disabling ? (
            <button style={{ ...s.btn(C.red) }} onClick={() => setDisabling(true)}>Désactiver</button>
          ) : (
            <form onSubmit={disable}>
              <div style={s.formGroup}>
                <label style={s.formLabel}>Code de ton app d'authentification (ou un code de secours)</label>
                <input style={{ ...s.input, marginTop: 4, maxWidth: 200 }} value={disableCode}
                       onChange={e => setDisableCode(e.target.value)} required autoFocus />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button style={{ ...s.btn(C.red) }} type="submit" disabled={busy}>Confirmer la désactivation</button>
                <button style={{ ...s.btn('#4A5470') }} type="button" onClick={() => { setDisabling(false); setDisableCode(''); }}>Annuler</button>
              </div>
            </form>
          )}
        </div>
      ) : setupData ? (
        <form onSubmit={confirmSetup}>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 14 }}>
            <img src={setupData.qrCodeDataUrl} alt="QR code MFA" style={{ width: 180, height: 180, borderRadius: 12, border: '1px solid var(--card-border)' }} />
            <div style={{ fontSize: 12.5, color: 'var(--text-mute)', maxWidth: 280 }}>
              <p>1. Scanne ce QR code avec ton app d'authentification.</p>
              <p>2. Ou saisis ce code manuellement :</p>
              <code style={{ display: 'block', background: 'var(--th-bg)', padding: '8px 10px', borderRadius: 8, wordBreak: 'break-all', fontSize: 11.5 }}>
                {setupData.secret}
              </code>
              <p style={{ marginTop: 10 }}>3. Entre le code à 6 chiffres généré pour confirmer.</p>
            </div>
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Code à 6 chiffres</label>
            <input style={{ ...s.input, marginTop: 4, maxWidth: 160, fontSize: 18, textAlign: 'center', letterSpacing: 4 }}
                   maxLength="6" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} required autoFocus />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button style={{ ...s.btn(C.green) }} type="submit" disabled={busy}>Confirmer et activer</button>
            <button style={{ ...s.btn('#4A5470') }} type="button" onClick={() => setSetupData(null)}>Annuler</button>
          </div>
        </form>
      ) : (
        <button style={{ ...s.btn(C.green) }} onClick={startSetup} disabled={busy}>
          🔑 Activer la double authentification
        </button>
      )}
    </div>
  );
}

function SecurityPanel({ currentUser }) {
  const [tab, setTab] = useState('overview');
  const [ov, setOv] = useState(null);
  const [locked, setLocked] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [failures, setFailures] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [auditActions, setAuditActions] = useState([]);
  const [auditFilter, setAuditFilter] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');

  const isSuper = !currentUser?.staff_role || currentUser.staff_role === 'admin';

  const loadAll = () => {
    api.get('/api/admin/security/overview').then(r => setOv(r.data)).catch(() => {});
    api.get('/api/admin/security/locked').then(r => setLocked(r.data.locked || [])).catch(() => {});
    api.get('/api/admin/security/sessions').then(r => setSessions(r.data.sessions || [])).catch(() => {});
    api.get('/api/admin/security/failures').then(r => setFailures(r.data.failures || [])).catch(() => {});
    if (isSuper) {
      api.get('/api/admin/security/audit' + (auditFilter ? `?action=${auditFilter}` : ''))
        .then(r => { setAuditRows(r.data.entries || []); setAuditActions(r.data.actions || []); })
        .catch(() => {});
    }
  };

  useAutoRefresh(loadAll, 30000, [auditFilter]);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 4000); };

  const unlock = async (contact) => {
    setBusy(contact);
    try {
      const { data } = await api.post('/api/admin/security/unlock', { contact });
      flash('✅ ' + data.message);
      loadAll();
    } catch (e) { flash('❌ ' + (e.response?.data?.error || 'Échec')); }
    finally { setBusy(''); }
  };

  const revoke = async (u) => {
    if (!window.confirm(`Déconnecter ${u.name || u.contact} de tous ses appareils ?`)) return;
    setBusy(u.id);
    try {
      const { data } = await api.post(`/api/admin/security/revoke/${u.id}`);
      flash('✅ ' + data.message);
      loadAll();
    } catch (e) { flash('❌ ' + (e.response?.data?.error || 'Échec')); }
    finally { setBusy(''); }
  };

  const revokeBio = async (u) => {
    if (!window.confirm(
      `Désactiver la biométrie de ${u.name || u.contact} ?\n\n` +
      `À faire si l'appareil est perdu ou volé : la clé biométrique donne un accès permanent au compte.`
    )) return;
    setBusy(u.id);
    try {
      const { data } = await api.post(`/api/admin/security/revoke-biometric/${u.id}`);
      flash('✅ ' + data.message);
      loadAll();
    } catch (e) { flash('❌ ' + (e.response?.data?.error || 'Échec')); }
    finally { setBusy(''); }
  };

  const tabs = [
    ['overview', '📈 Vue d\'ensemble'],
    ['mfa', '🔑 Ma double authentification'],
    ['locked', `🔒 Verrouillés${locked.length ? ` (${locked.length})` : ''}`],
    ['sessions', '📱 Sessions'],
    ['failures', '⚠️ Échecs'],
    ...(isSuper ? [['audit', '📜 Journal d\'audit']] : []),
  ];

  const fmt = (d) => d ? new Date(d).toLocaleString('fr-FR') : '—';

  return (
    <div>
      {msg && <div style={s.info(msg.startsWith('✅') ? 'green' : 'red')}>{msg}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {tabs.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{
              ...s.btn(tab === id ? C.green : '#4A5470'),
              opacity: tab === id ? 1 : 0.65, fontSize: 12,
            }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Ma double authentification ── */}
      {tab === 'mfa' && <MfaSetup />}

      {/* ── Vue d'ensemble ── */}
      {tab === 'overview' && ov && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12, marginBottom: 16 }}>
            <SecStat label="Contacts verrouillés" value={ov.lockedContacts} tone={ov.lockedContacts > 0 ? C.gold : C.green} />
            <SecStat label="Échecs (24 h)" value={ov.failures24h} tone={ov.failures24h > 50 ? C.red : C.green} />
            <SecStat label="IP suspectes" value={ov.suspiciousIps.length} tone={ov.suspiciousIps.length > 0 ? C.red : C.green} />
            <SecStat label="Comptes bloqués" value={ov.blockedAccounts} tone={C.gray} />
            <SecStat label="Sessions actives (30 j)" value={ov.activeSessions} tone={C.blue} />
          </div>

          {ov.suspiciousIps.length > 0 && (
            <div className="md-card" style={s.card}>
              <div style={s.cardTitle}><Ic icon={AlertTriangle} color={C.red} /> Adresses IP à surveiller</div>
              <div style={s.info('red')}>
                Ces IP cumulent au moins 10 échecs d'authentification sur 24 h.
                Plusieurs contacts distincts depuis une même IP évoque un balayage automatisé.
              </div>
              <table style={s.table}>
                <thead><tr>
                  <th style={s.th}>IP</th><th style={s.th}>Échecs</th>
                  <th style={s.th}>Contacts visés</th><th style={s.th}>Dernier</th>
                </tr></thead>
                <tbody>
                  {ov.suspiciousIps.map(r => (
                    <tr key={r.ip}>
                      <td className="mono" style={s.tdMono}>{r.ip}</td>
                      <td style={s.td}><strong style={{ color: C.red }}>{r.attempts}</strong></td>
                      <td style={s.td}>{r.distinct_contacts}</td>
                      <td style={s.td}>{fmt(r.last_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="md-card" style={s.card}>
            <div style={s.cardTitle}><Ic icon={Clock} color={'#6B7280'} /> Dernières connexions</div>
            <table style={s.table}>
              <thead><tr>
                <th style={s.th}>Utilisateur</th><th style={s.th}>Rôle</th>
                <th style={s.th}>Date</th><th style={s.th}>IP</th>
              </tr></thead>
              <tbody>
                {ov.recentLogins.map(u => (
                  <tr key={u.id}>
                    <td style={s.td}>{u.name || u.contact}</td>
                    <td style={s.td}>{u.role}</td>
                    <td style={s.td}>{fmt(u.last_login_at)}</td>
                    <td className="mono" style={s.tdMono}>{u.last_login_ip || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Verrouillés ── */}
      {tab === 'locked' && (
        <div className="md-card" style={s.card}>
          <div style={s.cardTitle}><Ic icon={Lock} color={C.red} /> Comptes verrouillés (trop de codes erronés)</div>
          <div style={s.info('blue')}>
            Après plusieurs codes erronés, le contact est verrouillé temporairement.
            Débloquer permet à la personne de redemander un code immédiatement.
          </div>
          {locked.length === 0
            ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-mute)', fontSize: 13 }}>
                Aucun compte verrouillé.
              </div>
            : <table style={s.table}>
                <thead><tr>
                  <th style={s.th}>Contact</th><th style={s.th}>Nom</th>
                  <th style={s.th}>Tentatives</th><th style={s.th}>Jusqu'à</th><th style={s.th}></th>
                </tr></thead>
                <tbody>
                  {locked.map(l => (
                    <tr key={l.contact}>
                      <td className="mono" style={s.tdMono}>{l.contact}</td>
                      <td style={s.td}>{l.name || '—'}</td>
                      <td style={s.td}><strong style={{ color: C.red }}>{l.attempts}</strong></td>
                      <td style={s.td}>{fmt(l.locked_until)}</td>
                      <td style={s.td}>
                        <button style={{ ...s.btn(), padding: '6px 12px', fontSize: 11 }}
                          disabled={busy === l.contact}
                          onClick={() => unlock(l.contact)}>
                          {busy === l.contact ? '⏳' : '🔓 Débloquer'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>}
        </div>
      )}

      {/* ── Sessions ── */}
      {tab === 'sessions' && (
        <div className="md-card" style={s.card}>
          <div style={s.cardTitle}><Ic icon={Smartphone} color={C.green} /> Sessions et appareils</div>
          <div style={s.info('gold')}>
            Révoquer déconnecte la personne de <strong>tous</strong> ses appareils.
            La clé biométrique donne un accès permanent : la retirer est indispensable
            si un téléphone est perdu ou volé.
          </div>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Utilisateur</th><th style={s.th}>Rôle</th>
              <th style={s.th}>Dernière connexion</th><th style={s.th}>IP</th>
              <th style={s.th}>Biométrie</th><th style={s.th}>Actions</th>
            </tr></thead>
            <tbody>
              {sessions.map(u => (
                <tr key={u.id}>
                  <td style={s.td}>{u.name || u.contact}</td>
                  <td style={s.td}>{u.staff_role || u.role}</td>
                  <td style={s.td}>{fmt(u.last_login_at)}</td>
                  <td className="mono" style={s.tdMono}>{u.last_login_ip || '—'}</td>
                  <td style={s.td}>{u.has_biometric ? '✅' : '—'}</td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={{ ...s.btn('#4A5470'), padding: '5px 9px', fontSize: 10 }}
                        disabled={busy === u.id} onClick={() => revoke(u)}>
                        Déconnecter
                      </button>
                      {u.has_biometric && (
                        <button style={{ ...s.btn(C.red), padding: '5px 9px', fontSize: 10 }}
                          disabled={busy === u.id} onClick={() => revokeBio(u)}>
                          Retirer biométrie
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Échecs ── */}
      {tab === 'failures' && (
        <div className="md-card" style={s.card}>
          <div style={s.cardTitle}><Ic icon={AlertCircle} color={'#D97706'} /> Échecs d'authentification</div>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Date</th><th style={s.th}>Contact</th>
              <th style={s.th}>IP</th><th style={s.th}>Motif</th>
            </tr></thead>
            <tbody>
              {failures.map((f, i) => (
                <tr key={i}>
                  <td style={s.td}>{fmt(f.created_at)}</td>
                  <td className="mono" style={s.tdMono}>{f.contact || '—'}</td>
                  <td className="mono" style={s.tdMono}>{f.ip || '—'}</td>
                  <td style={s.td}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 999,
                      background: f.reason === 'locked' ? C.gold + '22' : C.red + '22',
                      color: f.reason === 'locked' ? C.gold : C.red,
                    }}>
                      {{ bad_code: 'Code erroné', locked: 'Verrouillé',
                         bad_biometric: 'Biométrie invalide' }[f.reason] || f.reason}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {failures.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-mute)', fontSize: 13 }}>
              Aucun échec enregistré.
            </div>
          )}
        </div>
      )}

      {/* ── Audit ── */}
      {tab === 'audit' && isSuper && (
        <div className="md-card" style={s.card}>
          <div style={s.cardTitle}><Ic icon={FileText} color={'#6B7280'} /> Journal d'audit</div>
          <div style={s.info('blue')}>
            Trace qui a validé un KYC, activé un abonnement, bloqué un compte ou
            modifié la configuration. Indispensable en cas de contestation.
          </div>
          <select style={{ ...s.input, marginBottom: 12, maxWidth: 320 }}
            value={auditFilter} onChange={e => setAuditFilter(e.target.value)}>
            <option value="">Toutes les actions</option>
            {auditActions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Date</th><th style={s.th}>Auteur</th>
              <th style={s.th}>Action</th><th style={s.th}>Cible</th>
              <th style={s.th}>Détail</th><th style={s.th}>IP</th>
            </tr></thead>
            <tbody>
              {auditRows.map(a => (
                <tr key={a.id}>
                  <td style={s.td}>{fmt(a.created_at)}</td>
                  <td style={s.td}>{a.actor_name || a.actor_contact || '—'}</td>
                  <td className="mono" style={s.tdMono}>{a.action}</td>
                  <td className="mono" style={s.tdMono}>{a.target_type ? `${a.target_type}` : '—'}</td>
                  <td className="mono" style={s.tdMono}>
                    {a.detail ? JSON.stringify(a.detail).slice(0, 60) : '—'}
                  </td>
                  <td className="mono" style={s.tdMono}>{a.ip || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {auditRows.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-mute)', fontSize: 13 }}>
              Aucune entrée pour ce filtre.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SecStat({ label, value, tone }) {
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--card-border)', borderRadius: 16, padding: 16, boxShadow: 'var(--card-shadow)',
    }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: tone }}>{value}</div>
      <div style={{ fontSize: 10.5, color: 'var(--text-mute)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px' }}>
        {label}
      </div>
    </div>
  );
}

// ─── CONFIG ──────────────────────────────────────────────
const CONFIG_CATEGORIES = [
  { id: 'canaux',     label: '📡 Canaux & OTP' },
  { id: 'securite',   label: '🛡️ Sécurité & RGPD' },
  { id: 'dispatch',   label: '🎯 Dispatch' },
  { id: 'mobile',     label: '📲 App mobile' },
  { id: 'paiements',  label: '💳 Paiements & tarifs' },
];

function ConfigPanel() {
  // Le comptable a un accès en lecture seule, limité aux tarifs/paiements —
  // les identifiants Twilio/SendGrid/SMTP ne sont de toute façon jamais
  // renvoyés par l'API pour ce rôle (voir CONFIG_SECRET_FIELDS côté serveur).
  const staffRole = (() => {
    try { return JSON.parse(localStorage.getItem('admin_user'))?.staff_role; } catch { return null; }
  })();
  const isComptable = staffRole === 'comptable';
  const visibleCategories = isComptable
    ? CONFIG_CATEGORIES.filter(c => c.id === 'paiements')
    : CONFIG_CATEGORIES;

  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState('');
  const [cat, setCat] = useState(isComptable ? 'paiements' : 'canaux');

  const testEmail = async () => {
    setTesting(true); setTestMsg('');
    try {
      const { data } = await api.post('/api/admin/config/test-email', {});
      setTestMsg(`✅ ${data.message} — vérifie ta boîte (et les spams).`);
    } catch (err) {
      setTestMsg(`❌ ${err.response?.data?.error || 'Échec de l\'envoi'}`);
    } finally { setTesting(false); }
  };

  useEffect(() => {
    api.get('/api/admin/config').then(r => setConfig(r.data || {}));
  }, []);

  const [saveError, setSaveError] = useState('');

  const save = async () => {
    setSaving(true); setSaveError('');
    try {
      const { data } = await api.put('/api/admin/config', config);
      // On recharge l'état réellement enregistré : afficher « sauvegardé »
      // sans vérifier masquait les échecs silencieux.
      if (data?.config) setConfig(data.config);
      setSaved(data?.message || 'Configuration sauvegardée');
      setTimeout(() => setSaved(false), 4000);
    } catch (e) {
      setSaveError(e.response?.data?.error || 'Échec de la sauvegarde');
    } finally { setSaving(false); }
  };

  const field = (key, label, sub, type = 'text', placeholder = '') => (
    <div style={s.formGroup}>
      <div style={s.formLabel}>{label}</div>
      {sub && <div style={s.formSub}>{sub}</div>}
      <input style={{ ...s.input, marginTop: 4, fontFamily: type === 'password' ? 'monospace' : 'inherit',
                      opacity: isComptable ? 0.7 : 1 }}
             type={type} value={config[key] || ''} placeholder={placeholder} disabled={isComptable}
             onChange={e => setConfig(c => ({ ...c, [key]: e.target.value }))} />
    </div>
  );

  const toggle = (key, label) => (
    <label style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 12px', marginTop: 6, fontSize: 13,
      color: '#101426', fontWeight: 500,
      background: '#F5F7FC', border: '1px solid #E1E5EF', borderRadius: 10,
      cursor: 'pointer',
    }}>
      <input
        type="checkbox"
        checked={!!config[key]}
        disabled={isComptable}
        onChange={e => setConfig(c => ({ ...c, [key]: e.target.checked }))}
        style={{ width: 18, height: 18, accentColor: '#0E8AA8', cursor: isComptable ? 'default' : 'pointer' }}
      />
      <span>{label}</span>
    </label>
  );

  return (
    <div>
      {saved && <div style={s.info('green')}>✅ {typeof saved === 'string' ? saved : 'Configuration sauvegardée'}</div>}
      {saveError && <div style={s.info('red')}>❌ {saveError}</div>}

      {isComptable && (
        <div style={s.info('blue')}>👁️ Accès en lecture seule — contacte un administrateur pour modifier ces réglages.</div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
        {visibleCategories.map(c => (
          <button
            key={c.id}
            onClick={() => setCat(c.id)}
            style={{
              border: 'none', borderRadius: 999, cursor: 'pointer',
              padding: '9px 16px', fontSize: 12.5, fontWeight: 700,
              background: cat === c.id ? C.green : 'var(--surface)',
              color: cat === c.id ? '#fff' : 'var(--text-strong)',
              boxShadow: cat === c.id ? `0 2px 10px -3px ${C.green}88` : 'inset 0 0 0 1px var(--card-border)',
            }}
          >
            {c.label}
          </button>
        ))}
      </div>

      {cat === 'canaux' && (<>
        <div className="md-card" style={s.card}>
          <div style={s.cardTitle}><Ic icon={Shuffle} color={C.green} /> Activation des canaux</div>
          {toggle('whatsapp_otp_enabled', '📱 OTP par WhatsApp activé')}
          {toggle('email_otp_enabled', '📧 OTP par Email activé')}
          {toggle('manual_payment', '💳 Paiement manuel (Mobile Money) activé')}
        </div>

        <div className="md-card" style={s.card}>
          <div style={s.cardTitle}><Ic icon={Smartphone} color={'#16A34A'} /> OTP via WhatsApp (Twilio)</div>
          <div style={s.info('blue')}>📌 Créez un compte sur twilio.com → Activez WhatsApp Business.</div>
          <div style={s.formRow}>
            {field('twilio_sid', 'Twilio Account SID', '', 'password', 'ACxxxxxxxxxx')}
            {field('twilio_token', 'Twilio Auth Token', '', 'password', 'xxxxxxxxxx')}
          </div>
          {field('twilio_whatsapp_number', 'Numéro WhatsApp', 'Format : whatsapp:+14155238886', 'text', 'whatsapp:+14155238886')}
        </div>

        <div className="md-card" style={s.card}>
          <div style={s.cardTitle}><Ic icon={Mail} color={C.green} /> OTP via ton serveur mail (SMTP)</div>
          <div style={s.info('green')}>
            ✅ <strong>Recommandé</strong> — utilise le serveur mail de ton domaine (Hostinger, cPanel, Gmail…).
            Si ces champs sont remplis, ils ont <strong>priorité sur SendGrid</strong>.
          </div>
          <div style={s.formRow}>
            {field('smtp_host', '🖥️ Serveur SMTP', 'ex : smtp.hostinger.com', 'text', 'smtp.tondomaine.ci')}
            {field('smtp_port', '🔌 Port', '587 (STARTTLS) ou 465 (SSL)', 'number', '587')}
          </div>
          <div style={s.formRow}>
            {field('smtp_user', '👤 Utilisateur', 'Souvent l\'adresse complète', 'text', 'noreply@tondomaine.ci')}
            {field('smtp_pass', '🔑 Mot de passe', '', 'password', '••••••••')}
          </div>
          {field('smtp_from', '✉️ Adresse expéditeur', 'Vide = identique à l\'utilisateur', 'email', 'noreply@tondomaine.ci')}
          <button
            style={{ ...s.btn('#4A5470'), marginTop: 10 }}
            onClick={testEmail}
            disabled={testing || !config.smtp_host}
          >
            {testing ? '⏳ Envoi...' : '📨 Envoyer un e-mail de test'}
          </button>
          {testMsg && (
            <div style={s.info(testMsg.startsWith('✅') ? 'green' : 'red')}>{testMsg}</div>
          )}
          <div style={{ ...s.formSub, marginTop: 8 }}>
            💡 Sauvegarde la configuration avant de tester.
          </div>
        </div>

        <div className="md-card" style={s.card}>
          <div style={s.cardTitle}><Ic icon={Mail} color={C.green} /> OTP via Email (SendGrid) — alternative</div>
          <div style={s.info('blue')}>📌 sendgrid.com → Settings → API Keys. Utilisé uniquement si le SMTP ci-dessus est vide.</div>
          <div style={s.formRow}>
            {field('sendgrid_key', 'SendGrid API Key', '', 'password', 'SG.xxxxxxxxxx')}
            {field('sendgrid_from', 'Email expéditeur', 'Doit être vérifié', 'email', 'noreply@monartisanci.ci')}
          </div>
        </div>
      </>)}

      {cat === 'securite' && (<>
        <div className="md-card" style={s.card}>
          <div style={s.cardTitle}><Ic icon={Shield} color={C.red} /> RGPD & Sécurité</div>
          <div style={s.info('blue')}>
            💡 <strong>Collecte KYC</strong> : si désactivée, aucun document d'identité ne sera demandé aux artisans (conforme RGPD / loi ivoirienne 2013-450).
          </div>
          {toggle('kyc_enabled', '🪪 Collecte KYC activée (demande CNI/RCCM + selfie)')}
          {toggle('ai_kyc_enabled', '🤖 Vérification KYC automatique par IA (Claude)')}
          {toggle('support_ai_enabled', '🤖 Réponses IA dans le chat support (Claude) — s\'appuie sur vos réponses passées, jamais inventée')}

          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px dashed #E1E5EF' }}>
            <div style={s.formLabel}>🔔 Alerte quand un client demande un agent (mot « agent » dans le chat support)</div>
            <div style={s.formSub}>Canal utilisé pour te prévenir qu'une conversation attend une réponse humaine.</div>
            <select
              style={{ ...s.input, marginTop: 6 }}
              value={config.support_notify_channel || 'whatsapp'}
              onChange={e => setConfig(c => ({ ...c, support_notify_channel: e.target.value }))}
            >
              <option value="whatsapp">💬 WhatsApp uniquement</option>
              <option value="email">📧 Email uniquement</option>
              <option value="both">🔔 WhatsApp + Email</option>
            </select>
            {field('security_alert_email', '📧 Email d\'alerte admin', 'Utilisé si le canal Email est actif ci-dessus', 'email', 'toi@example.com')}
          </div>

          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px dashed #E1E5EF' }}>
            <div style={s.formLabel}>👆 Mode biométrie (empreinte / Face ID)</div>
            <div style={s.formSub}>Contrôle l'utilisation de la biométrie dans l'app mobile (clients + artisans).</div>
            <select
              style={{ ...s.input, marginTop: 6 }}
              value={config.biometric_mode || 'optional'}
              onChange={e => setConfig(c => ({ ...c, biometric_mode: e.target.value }))}
            >
              <option value="disabled">❌ Désactivée (option cachée dans l'app)</option>
              <option value="optional">🔓 Optionnelle (l'utilisateur choisit) — recommandé</option>
              <option value="required">🔒 Obligatoire (forcée pour tous les utilisateurs)</option>
            </select>
          </div>
        </div>

        <div className="md-card" style={s.card}>
          <div style={s.cardTitle}><Ic icon={Lock} color={C.red} /> Politique de sécurité</div>
          <div style={s.info('gold')}>
            ⚠️ Assouplir ces seuils facilite les attaques par essais successifs.
            5 tentatives et 15 minutes de verrouillage sont un bon équilibre.
          </div>
          <div style={s.formRow}>
            {field('otp_max_attempts', '🔢 Tentatives de code autorisées', 'Avant verrouillage', 'number', '5')}
            {field('otp_lock_minutes', '⏱️ Durée du verrouillage (min)', '', 'number', '15')}
          </div>
          <div style={s.formRow}>
            {field('otp_ttl_minutes', '⌛ Validité du code (min)', '', 'number', '10')}
            {field('session_idle_minutes', '💤 Inactivité avant verrouillage app (min)', '', 'number', '5')}
          </div>
          {field('session_ttl_days', '📅 Durée de session « rester connecté » (jours)', '', 'number', '30')}

          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px dashed #E1E5EF' }}>
            {toggle('autoblock_enabled', '🚫 Blocage automatique des comptes/IP abusifs activé')}
            {field('autoblock_threshold', 'Seuil avant blocage automatique', 'Nombre d\'échecs suspects avant déclenchement', 'number', '10')}
          </div>
        </div>
      </>)}

      {cat === 'dispatch' && (
        <div className="md-card" style={s.card}>
          <div style={s.cardTitle}><Ic icon={Target} color={C.green} /> Dispatch des demandes</div>
          <div style={s.info('green')}>
            Quand le client n'choisit pas de dépanneur, la demande part vers les plus
            proches et disponibles. Le premier qui accepte remporte la course.
          </div>
          {toggle('dispatch_enabled', '🎯 Dispatch automatique activé')}
          <div style={s.formRow}>
            {field('dispatch_candidates', '👥 Dépanneurs notifiés par demande', 'Trop peu = pas de réponse ; trop = spam', 'number', '5')}
            {field('dispatch_radius_km', '📍 Rayon de diffusion (km)', '', 'number', '15')}
          </div>
          {field('dispatch_expiry_min', '⏳ Expiration d\'une offre (min)', 'Passé ce délai la demande est close', 'number', '10')}
        </div>
      )}

      {cat === 'mobile' && (
        <div className="md-card" style={s.card}>
          <div style={s.cardTitle}><Ic icon={Smartphone} color={C.green} /> Fonctionnalités apps mobile</div>
          <div style={s.info('blue')}>
            💡 Active ou désactive les fonctionnalités côté apps client + artisan sans rebuild.
          </div>
          {toggle('maps_enabled', '🗺️ Carte GPS (Google Maps) activée — apps client & artisan')}
          {toggle('inapp_notifications_enabled', '🔔 Notifications in-app activées (toasts / bannières internes)')}
        </div>
      )}

      {cat === 'paiements' && (
        <div className="md-card" style={{ ...s.card, marginBottom: 16 }}>
          <div style={s.cardTitle}><Ic icon={Rocket} color={'#D97706'} /> Mode lancement</div>
          <div style={s.info(config.premium_launch_mode ? 'gold' : 'blue')}>
            {config.premium_launch_mode
              ? '✅ Actif : chaque dépanneur qui s\'inscrit reçoit le Premium gratuitement, et l\'écran de paiement de l\'app n\'affiche plus aucun tarif. Désactive ce mode quand la phase de lancement sera terminée.'
              : '⏸️ Inactif : les nouveaux dépanneurs ne reçoivent pas le Premium automatiquement, l\'app affiche les tarifs normalement.'}
          </div>
          {toggle('premium_launch_mode', '🎁 Offrir le Premium à tous les dépanneurs qui s\'inscrivent')}
          {field('premium_launch_duration_days', '⏳ Durée du Premium offert (jours)', 'Par défaut 730 jours (2 ans) — aucune expiration automatique n\'existe ailleurs, cette durée n\'est qu\'informative dans l\'app.', 'number', '730')}
        </div>
      )}

      {cat === 'paiements' && (
        <div className="md-card" style={s.card}>
          <div style={s.cardTitle}><Ic icon={CreditCard} color={'#16A34A'} /> Paiements Mobile Money</div>
          <div style={s.info('gold')}>💡 Ces numéros s'affichent dans l'app mobile.</div>
          <div style={s.formRow}>
            {field('wave_number', '🌊 Wave', '', 'tel', '07 XX XX XX XX')}
            {field('orange_number', '🟠 Orange Money', '', 'tel', '07 XX XX XX XX')}
          </div>
          <div style={s.formRow}>
            {field('mtn_number', '📱 MTN MoMo', '', 'tel', '05 XX XX XX XX')}
            {field('admin_whatsapp', '💬 WhatsApp Admin', '', 'tel', '+225 07 XX XX XX')}
          </div>
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #E1E5EF' }}>
            <div style={s.formLabel}>💰 Tarifs des abonnements (FCFA)</div>
            <div style={s.formSub}>Le client choisira son plan dans l'app (mensuel / trimestriel / 6 mois / annuel).</div>
          </div>
          <div style={s.formRow}>
            {field('prix_mensuel', '📅 Mensuel (1 mois)', '', 'number', '9900')}
            {field('prix_trimestriel', '📅 Trimestriel (3 mois)', '', 'number', '27000')}
          </div>
          <div style={s.formRow}>
            {field('prix_semestriel', '📅 Semestriel (6 mois)', '', 'number', '52000')}
            {field('prix_annuel', '📅 Annuel (12 mois)', '', 'number', '99000')}
          </div>
          <div style={{ marginTop: 8 }}>
            <div style={s.formLabel}>⭐ Plan mis en avant dans l'app</div>
            <select
              style={{ ...s.input, marginTop: 4, opacity: isComptable ? 0.7 : 1 }}
              value={config.plan_recommande || 'annuel'}
              disabled={isComptable}
              onChange={e => setConfig(c => ({ ...c, plan_recommande: e.target.value }))}
            >
              <option value="mensuel">Mensuel</option>
              <option value="trimestriel">Trimestriel</option>
              <option value="semestriel">Semestriel (6 mois)</option>
              <option value="annuel">Annuel (recommandé)</option>
            </select>
          </div>
          {field('payment_link', '🔗 Lien CinetPay (optionnel)', '', 'url', 'https://checkout.cinetpay.com/...')}
        </div>
      )}

      {!isComptable && (
        <button style={{ ...s.btn(), padding: '12px 28px', fontSize: 14 }} onClick={save} disabled={saving}>
          {saving ? '⏳ Sauvegarde...' : '💾 Sauvegarder la configuration'}
        </button>
      )}
    </div>
  );
}

// ─── BANNER ──────────────────────────────────────────────
function BannerPanel() {
  const [b, setB] = useState({
    enabled: false, title: '', message: '', cta_label: '', cta_url: '',
    target: 'all', tone: 'info', dismissible: true, starts_at: '', ends_at: '',
    audio_url: '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);

  const onAudioPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingAudio(true);
    try {
      const fd = new FormData();
      fd.append('audio', f);
      const r = await api.post('/api/uploads/audio', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setB(x => ({ ...x, audio_url: r.data.url }));
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally { setUploadingAudio(false); e.target.value = ''; }
  };

  useEffect(() => {
    api.get('/api/admin/banner').then(r => {
      const d = r.data || {};
      setB(x => ({
        ...x,
        ...d,
        starts_at: d.starts_at ? String(d.starts_at).slice(0, 16) : '',
        ends_at:   d.ends_at   ? String(d.ends_at).slice(0, 16)   : '',
      }));
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/banner', b);
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } finally { setSaving(false); }
  };

  const toneColors = {
    info:    { bg: '#DDE5FF', border: '#7FA6FF', color: '#1E3FCC' },
    promo:   { bg: '#fef3c7', border: '#fbbf24', color: '#92400e' },
    warning: { bg: '#fee2e2', border: '#f87171', color: '#991b1b' },
    success: { bg: '#D7EEF5', border: '#0E8AA8', color: '#0B6E85' },
  };
  const preview = toneColors[b.tone] || toneColors.info;

  return (
    <div>
      {saved && <div style={s.info('green')}>✅ Bannière sauvegardée !</div>}

      <div className="md-card" style={s.card}>
        <div style={s.cardTitle}><Ic icon={Megaphone} color={'#D97706'} /> Bannière d'information / publicité</div>
        <div style={s.info('blue')}>
          💡 Cette bannière s'affiche en haut de l'écran d'accueil (clients) et du tableau de bord (artisans).
          Utilise-la pour annoncer une promo, une maintenance, une nouveauté…
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', fontSize: 13, fontWeight: 600 }}>
          <input type="checkbox" checked={!!b.enabled}
                 onChange={e => setB({ ...b, enabled: e.target.checked })} />
          Bannière activée (visible dans l'application)
        </label>

        {/* Preview */}
        {b.message && (
          <div style={{
            background: preview.bg, border: `1px solid ${preview.border}`,
            color: preview.color, borderRadius: 12, padding: 14,
            marginBottom: 14, fontSize: 13,
          }}>
            <div style={{ fontSize: 10, fontWeight: 800, opacity: 0.7, marginBottom: 4, letterSpacing: '0.5px' }}>
              APERÇU {b.enabled ? '' : '(désactivée)'}
            </div>
            {b.title && <div style={{ fontWeight: 800, marginBottom: 4 }}>{b.title}</div>}
            <div>{b.message}</div>
            {b.cta_label && (
              <div style={{ marginTop: 6, fontWeight: 700, textDecoration: 'underline' }}>
                {b.cta_label} →
              </div>
            )}
          </div>
        )}

        <div style={s.formGroup}>
          <div style={s.formLabel}>Titre (optionnel)</div>
          <input style={{ ...s.input, marginTop: 4 }} value={b.title || ''}
                 placeholder="Ex : 🎉 Promo de lancement"
                 onChange={e => setB({ ...b, title: e.target.value })} />
        </div>

        <div style={{ ...s.formGroup, marginTop: 12 }}>
          <div style={s.formLabel}>Message *</div>
          <textarea style={{ ...s.input, marginTop: 4, minHeight: 70, fontFamily: 'inherit', resize: 'vertical' }}
                    value={b.message || ''}
                    placeholder="Texte affiché aux utilisateurs"
                    onChange={e => setB({ ...b, message: e.target.value })} />
        </div>

        <div style={{ ...s.formRow, marginTop: 12 }}>
          <div style={s.formGroup}>
            <div style={s.formLabel}>Libellé bouton (optionnel)</div>
            <input style={{ ...s.input, marginTop: 4 }} value={b.cta_label || ''}
                   placeholder="Ex : En savoir plus"
                   onChange={e => setB({ ...b, cta_label: e.target.value })} />
          </div>
          <div style={s.formGroup}>
            <div style={s.formLabel}>URL bouton (optionnel)</div>
            <input style={{ ...s.input, marginTop: 4 }} value={b.cta_url || ''}
                   placeholder="https://… ou tel:… ou https://wa.me/…"
                   onChange={e => setB({ ...b, cta_url: e.target.value })} />
          </div>
        </div>

        <div style={{ ...s.formRow, marginTop: 12 }}>
          <div style={s.formGroup}>
            <div style={s.formLabel}>Cible</div>
            <div style={s.formSub}>Qui voit la bannière ?</div>
            <select style={{ ...s.input, marginTop: 4 }} value={b.target || 'all'}
                    onChange={e => setB({ ...b, target: e.target.value })}>
              <option value="all">👥 Tout le monde</option>
              <option value="client">🙋 Clients uniquement</option>
              <option value="artisan">🔧 Artisans uniquement</option>
            </select>
          </div>
          <div style={s.formGroup}>
            <div style={s.formLabel}>Style</div>
            <div style={s.formSub}>Couleur de fond de la bannière</div>
            <select style={{ ...s.input, marginTop: 4 }} value={b.tone || 'info'}
                    onChange={e => setB({ ...b, tone: e.target.value })}>
              <option value="info">ℹ️ Info (bleu)</option>
              <option value="promo">🎉 Promo (ocre)</option>
              <option value="warning">⚠️ Alerte (rouge)</option>
              <option value="success">✅ Succès (vert)</option>
            </select>
          </div>
        </div>

        <div style={{ ...s.formRow, marginTop: 12 }}>
          <div style={s.formGroup}>
            <div style={s.formLabel}>Démarre le (optionnel)</div>
            <input style={{ ...s.input, marginTop: 4 }} type="datetime-local"
                   value={b.starts_at || ''}
                   onChange={e => setB({ ...b, starts_at: e.target.value })} />
          </div>
          <div style={s.formGroup}>
            <div style={s.formLabel}>Se termine le (optionnel)</div>
            <input style={{ ...s.input, marginTop: 4 }} type="datetime-local"
                   value={b.ends_at || ''}
                   onChange={e => setB({ ...b, ends_at: e.target.value })} />
          </div>
        </div>

        {/* Note vocale */}
        <div style={{ ...s.formGroup, marginTop: 12 }}>
          <div style={s.formLabel}>🎙️ Note vocale (optionnelle)</div>
          <div style={s.formSub}>
            Joins un message audio (≤ 15 Mo) lu directement dans l'app à côté du texte.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
            <label style={{ ...s.btn(C.blue), cursor: 'pointer', display: 'inline-block' }}>
              {uploadingAudio ? '⏳ Upload…' : (b.audio_url ? '🔁 Remplacer' : '➕ Choisir un fichier audio')}
              <input type="file" accept="audio/*" style={{ display: 'none' }}
                     onChange={onAudioPick} disabled={uploadingAudio} />
            </label>
            {b.audio_url && (
              <>
                <audio controls src={b.audio_url} style={{ height: 36 }} />
                <button style={s.btn(C.red)} onClick={() => setB(x => ({ ...x, audio_url: '' }))}>
                  🗑 Retirer
                </button>
              </>
            )}
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', fontSize: 12, marginTop: 6 }}>
          <input type="checkbox" checked={!!b.dismissible}
                 onChange={e => setB({ ...b, dismissible: e.target.checked })} />
          L'utilisateur peut masquer la bannière
        </label>

        <button style={{ ...s.btn(), padding: '12px 28px', fontSize: 14, marginTop: 10 }}
                onClick={save} disabled={saving || (!b.message && !b.audio_url)}>
          {saving ? '⏳ Sauvegarde...' : '💾 Sauvegarder la bannière'}
        </button>
      </div>
    </div>
  );
}

// ─── NOTIFICATIONS (accusé de réception obligatoire) ──────
function NotificationsPanel() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ title: '', message: '', target: 'all', tone: 'info' });
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');

  const load = () => {
    api.get('/api/admin/notifications').then(r => setList(r.data.notifications || [])).catch(() => {});
  };
  useAutoRefresh(load, 30000, []);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 4000); };

  const send = async () => {
    if (!form.message.trim()) return;
    setSending(true);
    try {
      await api.post('/api/admin/notifications', form);
      setForm({ title: '', message: '', target: 'all', tone: 'info' });
      flash('✅ Notification envoyée');
      load();
    } catch (e) { flash('❌ ' + (e.response?.data?.error || 'Échec')); }
    finally { setSending(false); }
  };

  const toggleActive = async (n) => {
    setBusy(n.id);
    try {
      await api.put(`/api/admin/notifications/${n.id}`, { active: !n.active });
      load();
    } catch (e) { flash('❌ ' + (e.response?.data?.error || 'Échec')); }
    finally { setBusy(''); }
  };

  const toneLabel = { info: 'ℹ️ Info', warning: '⚠️ Avertissement', success: '✅ Succès', promo: '🏷️ Promo' };
  const targetLabel = { all: 'Tout le monde', client: 'Clients', artisan: 'Dépanneurs' };

  return (
    <div>
      {msg && <div style={s.info(msg.startsWith('✅') ? 'green' : 'red')}>{msg}</div>}

      <div className="md-card" style={s.card}>
        <div style={s.cardTitle}><Ic icon={Bell} color={C.green} /> Nouvelle notification</div>
        <div style={s.info('blue')}>
          💡 Contrairement à la bannière, cette notification apparaît en <strong>pop-up bloquant</strong> —
          le client ou le dépanneur doit cliquer sur « OK, j'ai compris » pour la faire disparaître.
          Utilise-la pour une information importante à ne pas manquer (changement de règles, maintenance, obligation légale…).
        </div>
        <div style={s.formGroup}>
          <div style={s.formLabel}>Titre (optionnel)</div>
          <input style={{ ...s.input, marginTop: 4 }} value={form.title}
                 placeholder="Ex : Mise à jour importante"
                 onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
        </div>
        <div style={{ ...s.formGroup, marginTop: 12 }}>
          <div style={s.formLabel}>Message *</div>
          <textarea style={{ ...s.input, marginTop: 4, minHeight: 80, fontFamily: 'inherit', resize: 'vertical' }}
                    value={form.message}
                    placeholder="Texte que la personne devra lire avant de pouvoir continuer"
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
        </div>
        <div style={{ ...s.formRow, marginTop: 12 }}>
          <div style={s.formGroup}>
            <div style={s.formLabel}>Destinataires</div>
            <select style={{ ...s.input, marginTop: 4 }} value={form.target}
                    onChange={e => setForm(f => ({ ...f, target: e.target.value }))}>
              <option value="all">Tout le monde</option>
              <option value="client">Clients uniquement</option>
              <option value="artisan">Dépanneurs uniquement</option>
            </select>
          </div>
          <div style={s.formGroup}>
            <div style={s.formLabel}>Ton</div>
            <select style={{ ...s.input, marginTop: 4 }} value={form.tone}
                    onChange={e => setForm(f => ({ ...f, tone: e.target.value }))}>
              <option value="info">ℹ️ Info</option>
              <option value="warning">⚠️ Avertissement</option>
              <option value="success">✅ Succès</option>
              <option value="promo">🏷️ Promo</option>
            </select>
          </div>
        </div>
        <button style={{ ...s.btn(), padding: '12px 28px', fontSize: 14, marginTop: 14 }}
                onClick={send} disabled={sending || !form.message.trim()}>
          {sending ? '⏳ Envoi...' : '📤 Envoyer la notification'}
        </button>
      </div>

      <div className="md-card" style={s.card}>
        <div style={s.cardTitle}><Ic icon={FileText} color={'#6B7280'} /> Notifications envoyées</div>
        {!list.length ? (
          <div style={{ color: 'var(--text-mute)', fontSize: 13 }}>Aucune notification envoyée pour l'instant.</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Message</th>
                <th style={s.th}>Cible</th>
                <th style={s.th}>Ton</th>
                <th style={s.th}>Lu</th>
                <th style={s.th}>Envoyée</th>
                <th style={s.th}>État</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map(n => (
                <tr key={n.id}>
                  <td style={s.td}>
                    {n.title && <div style={{ fontWeight: 700 }}>{n.title}</div>}
                    <div style={{ color: 'var(--text-mute)', fontSize: 12 }}>{n.message}</div>
                  </td>
                  <td style={s.td}>{targetLabel[n.target] || n.target}</td>
                  <td style={s.td}>{toneLabel[n.tone] || n.tone}</td>
                  <td style={s.td}>{n.ack_count} / {n.eligible_count}</td>
                  <td style={s.td}>{new Date(n.created_at).toLocaleString('fr-FR')}</td>
                  <td style={s.td}>{n.active ? '🟢 Active' : '⚪ Désactivée'}</td>
                  <td style={s.td}>
                    <button style={{ ...s.btn(n.active ? '#4A5470' : C.green), fontSize: 11, padding: '6px 12px' }}
                            onClick={() => toggleActive(n)} disabled={busy === n.id}>
                      {n.active ? 'Désactiver' : 'Réactiver'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/** Bascule à `true` sous le seuil (téléphone + petite tablette) — la sidebar
 *  fixe en colonne n'a plus la place, elle devient un tiroir escamotable. */
function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

// ─── APP ─────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('admin_user')); } catch { return null; }
  });
  const [page, setPage] = useState('stats');
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEffect(() => { if (!isMobile) setSidebarOpen(false); }, [isMobile]);

  // Thème : préférence mémorisée, sinon on suit le réglage du système.
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('admin_theme');
    if (saved === 'light' || saved === 'dark') return saved;
    return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  });

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem('admin_theme', theme);
  }, [theme]);

  // Rafraîchit le user (et donc staff_role) depuis le serveur au montage,
  // pour refléter une éventuelle modification de niveau par un super-admin.
  useEffect(() => {
    if (!user) return;
    api.get('/api/auth/me')
      .then(({ data }) => {
        if (data?.user) {
          setUser(data.user);
          localStorage.setItem('admin_user', JSON.stringify(data.user));
        }
      })
      .catch(() => { /* silencieux : token invalide ⇒ déconnexion auto à la prochaine 401 */ });
  }, []); // eslint-disable-line

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setUser(null);
  };

  if (!user) return <LoginPage onLogin={setUser} />;

  const allNav = [
    { id: 'stats',    label: 'Tableau de bord', ico: LayoutDashboard, color: C.green, section: 'Général' },
    { id: 'kyc',      label: 'Validations KYC', ico: BadgeCheck,     color: C.gold,  section: 'Général' },
    { id: 'payments', label: 'Paiements',       ico: CreditCard,     color: '#16A34A', section: 'Général' },
    { id: 'reports',  label: 'Signalements',    ico: AlertTriangle,  color: C.red,   section: 'Général' },
    { id: 'litiges',  label: 'Litiges',         ico: Scale,          color: '#D97706', section: 'Général' },
    { id: 'support',  label: 'Support client',  ico: MessageCircle,  color: C.green, section: 'Général' },
    { id: 'artisans', label: 'Artisans',        ico: Wrench,         color: C.green, section: 'Gestion' },
    { id: 'metiers',  label: 'Métiers',         ico: Hammer,         color: C.gold,  section: 'Gestion' },
    { id: 'revenue',  label: 'Revenus artisans', ico: Wallet,        color: '#16A34A', section: 'Gestion' },
    { id: 'comptabilite', label: 'Comptabilité analytique', ico: Calculator, color: C.green, section: 'Gestion' },
    { id: 'users',    label: 'Utilisateurs',    ico: Users,          color: C.green, section: 'Gestion' },
    { id: 'banner',   label: 'Bannière',        ico: Megaphone,      color: '#D97706', section: 'Gestion' },
    { id: 'notifications', label: 'Notifications', ico: Bell,        color: C.green, section: 'Gestion' },
    { id: 'staff',    label: 'Équipe',          ico: Users,          color: C.gold,  section: 'Administration' },
    { id: 'security', label: 'Sécurité',        ico: ShieldCheck,    color: C.red,   section: 'Administration' },
    { id: 'legal',    label: 'CGU & mentions',  ico: FileText,       color: '#6B7280', section: 'Administration' },
    { id: 'config',   label: 'Configuration',   ico: Settings,       color: '#6B7280', section: 'Administration' },
  ];

  // Niveaux d'accès par staff_role.
  // NULL ou 'admin' = super-admin → accès total.
  const ALLOWED_BY_ROLE = {
    comptable: ['stats', 'payments', 'revenue', 'config', 'comptabilite'],
    support:   ['stats', 'reports', 'litiges', 'users', 'support', 'notifications', 'security'],
    suivi:     ['stats', 'kyc', 'artisans', 'metiers', 'banner'],
  };
  const sr = user.staff_role;
  const nav = (!sr || sr === 'admin')
    ? allNav
    : allNav.filter(n => (ALLOWED_BY_ROLE[sr] || []).includes(n.id));

  // Si la page courante n'est plus permise, replier sur la première autorisée.
  if (nav.length && !nav.find(n => n.id === page)) {
    setTimeout(() => setPage(nav[0].id), 0);
  }

  const pages = {
    stats: <StatsPanel />, kyc: <KYCPanel />, payments: <PaymentsPanel />,
    reports: <ReportsPanel />, litiges: <ModerationPanel />, support: <SupportPanel />,
    artisans: <ArtisansPanel />, metiers: <MetiersPanel />, revenue: <RevenuePanel />, users: <UsersPanel />,
    comptabilite: <ComptabilitePanel />,
    banner: <BannerPanel />,
    notifications: <NotificationsPanel />,
    staff: <StaffPanel currentUser={user} />,
    security: <SecurityPanel currentUser={user} />,
    legal: <LegalPanel currentUser={user} />,
    config: <ConfigPanel />,
  };
  const sections = [...new Set(nav.map(n => n.section))];

  return (
    <div style={s.app}>
      <div style={isMobile ? { ...s.topbar, padding: '10px 12px', gap: 10 } : s.topbar}>
        {isMobile && (
          <button
            title="Menu"
            aria-label="Ouvrir le menu"
            onClick={() => setSidebarOpen(o => !o)}
            style={{
              width: 34, height: 34, borderRadius: 10, cursor: 'pointer', flexShrink: 0,
              border: '1px solid var(--shell-border)', background: 'transparent',
              color: 'var(--shell-text)', fontSize: 16, lineHeight: 1,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>
            ☰
          </button>
        )}
        <div style={{ ...s.brand, ...(isMobile ? { fontSize: 13.5 } : {}) }}>
          <div style={s.brandIco}><Wrench size={16} color="#FFFFFF" strokeWidth={2.5} /></div>Mon Dépanneur CI
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12 }}>
          {!isMobile && (
            <span style={{ fontSize: 12, color: 'var(--text-mute)' }}>Connecté : {user.contact}</span>
          )}
          {(() => {
            const meta = roleMeta(user.staff_role || 'admin');
            return (
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999,
                background: meta.color + '22', color: meta.color, border: `1px solid ${meta.color}55`,
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
                <Ic icon={meta.ico} color={meta.color} size={12} />{meta.label}
              </span>
            );
          })()}
          <button
            title={theme === 'dark' ? 'Passer en thème clair' : 'Passer en thème sombre'}
            aria-label={theme === 'dark' ? 'Passer en thème clair' : 'Passer en thème sombre'}
            onClick={() => setTheme(t => (t === 'dark' ? 'light' : 'dark'))}
            style={{
              width: 34, height: 34, borderRadius: 999, cursor: 'pointer',
              border: '1px solid var(--shell-border)',
              background: 'transparent', color: 'var(--shell-text)',
              fontSize: 15, lineHeight: 1,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
          <button style={{ ...s.btn(C.red), padding: '6px 14px', fontSize: 11 }} onClick={logout}>Déconnexion</button>
        </div>
      </div>
      <div style={s.layout}>
        {isMobile && sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 29,
            }}
          />
        )}
        <div
          style={
            isMobile
              ? {
                  ...s.sidebar,
                  position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 30,
                  boxShadow: sidebarOpen ? '0 0 40px -8px rgba(0,0,0,.5)' : 'none',
                  transform: `translateX(${sidebarOpen ? '0' : '-100%'})`,
                  transition: 'transform 220ms ease',
                }
              : s.sidebar
          }
        >
          {sections.map(sec => (
            <div key={sec}>
              <div style={s.sbSection}>{sec}</div>
              {nav.filter(n => n.section === sec).map(n => (
                <div
                  key={n.id}
                  className="md-nav-item"
                  style={s.sbItem(page === n.id)}
                  onClick={() => { setPage(n.id); setSidebarOpen(false); }}
                >
                  <Ic icon={n.ico} color={page === n.id ? n.color : 'currentColor'} size={15} />{n.label}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div style={isMobile ? { ...s.main, padding: 14 } : s.main}>
          <PageErrorBoundary key={page}>{pages[page]}</PageErrorBoundary>
        </div>
      </div>
    </div>
  );
}
